import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { getApiHeaders, getTenantDb } from '../../utils/apiHelpers';
import { SelezioneBar, SelezioneCheckbox } from './SelezioneEtichette';
import { caricaPermessoModifica, salvaModificaEtichetta } from './modificaEtichetta';
import { BadgeRichiamo } from './richiami/BadgeRichiamo';
import { 
  Plus, Search, Check, X, Edit, Edit2, Trash2, 
  Import, PlusCircle, QrCode, Download, Settings, Eye,
  Package, AlertTriangle, Printer, ChevronLeft, ChevronRight,
  Calendar, FileText, Link2, Upload, RefreshCw, History,
  Mic, Copy, ChevronDown, ChevronUp
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Data ISO -> italiana (2025-12-31 -> 31/12/2025)
const formattaDataIt = (d) => {
  if (!d) return '';
  const s = String(d);
  if (s.includes('-') && s.length >= 10) {
    const [y, m, g] = s.slice(0, 10).split('-');
    return `${g}/${m}/${y}`;
  }
  return s;
};

// Risultati DDT/fatture raggruppati per prodotto, ogni gruppo con gli ultimi 5 arrivi (dal più recente)
const GruppiDdtRisultati = ({ gruppi, risultati, onSelect, testIdPrefix = 'gruppo-ddt' }) => {
  let gruppiEff = gruppi || [];
  if (gruppiEff.length === 0 && (risultati || []).length > 0) {
    const map = new Map();
    for (const r of risultati) {
      const k = (r.nome_prodotto || '').trim().toLowerCase();
      if (!map.has(k)) map.set(k, { prodotto: r.nome_prodotto, fornitore: r.fornitore, num_arrivi: 0, arrivi: [] });
      const g = map.get(k);
      g.num_arrivi += 1;
      if (g.arrivi.length < 5) g.arrivi.push(r);
    }
    gruppiEff = [...map.values()];
  }
  if (gruppiEff.length === 0) return null;
  return (
    <div className="space-y-3">
      {gruppiEff.map((g, gidx) => (
        <div key={gidx} data-testid={`${testIdPrefix}-${gidx}`} className="rounded-xl border border-slate-700/60 lg:border-gray-200 overflow-hidden">
          <div className="px-3 py-2 bg-slate-800/90 lg:bg-gray-100 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-white lg:text-gray-800 truncate">{g.prodotto}</p>
            <span className="text-[10px] text-slate-400 lg:text-gray-500 whitespace-nowrap flex-shrink-0">
              {g.num_arrivi > 5 ? `ultimi 5 di ${g.num_arrivi}` : `${g.num_arrivi} arriv${g.num_arrivi === 1 ? 'o' : 'i'}`}
            </span>
          </div>
          {(g.arrivi || []).map((item, idx) => (
            <button
              key={idx}
              onClick={() => onSelect(item)}
              data-testid={`${testIdPrefix}-${gidx}-arrivo-${idx}`}
              className="w-full text-left px-3 py-2 bg-black/40 lg:bg-white hover:bg-slate-700 lg:hover:bg-green-50 border-t border-slate-700/40 lg:border-gray-100 transition-colors"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className="font-semibold text-green-400 lg:text-green-700">{formattaDataIt(item.data_ddt)}</span>
                {item.fornitore && <span className="text-slate-300 lg:text-gray-600 truncate max-w-[140px]">{item.fornitore}</span>}
                {item.numero_lotto && <span className="bg-blue-500/20 lg:bg-blue-100 text-blue-400 lg:text-blue-700 px-1.5 py-0.5 rounded">Lotto {item.numero_lotto}</span>}
                {item.scadenza && <span className="bg-orange-500/20 lg:bg-orange-100 text-orange-400 lg:text-orange-700 px-1.5 py-0.5 rounded">Scad {formattaDataIt(item.scadenza)}</span>}
                {item.ddt_numero && <span className="text-slate-500 lg:text-gray-400">{item.tipo === 'fattura' ? 'Fatt.' : 'DDT'} #{item.ddt_numero}</span>}
              </div>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============ ALLERGENI UE (Reg. 1169/2011) ============
const ALLERGENI_UE_LIST = ['Glutine', 'Crostacei', 'Uova', 'Pesce', 'Arachidi', 'Soia', 'Latte', 'Frutta A Guscio', 'Sedano', 'Senape', 'Sesamo', 'Anidride Solforosa E Solfiti', 'Lupini', 'Molluschi'];

// Editor allergeni: mostra i rilevati automaticamente e permette di aggiungere/rimuovere
const AllergeniEditor = ({ selezionati, auto, onToggle }) => (
  <div data-testid="allergeni-editor" className="bg-orange-600/10 lg:bg-orange-50 rounded-xl p-3 border border-orange-500/30 lg:border-orange-200 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-semibold text-orange-400 lg:text-orange-800">Allergeni (Reg. UE 1169/2011)</p>
      <span className="text-[10px] text-orange-400/80 lg:text-orange-600" data-testid="allergeni-counter">
        {selezionati.length} selezionati{auto.length > 0 ? ` • ${auto.length} auto` : ''}
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {ALLERGENI_UE_LIST.map((a) => {
        const attivo = selezionati.includes(a);
        const isAuto = auto.includes(a);
        return (
          <button
            key={a}
            type="button"
            onClick={() => onToggle(a)}
            data-testid={`allergene-chip-${a.toLowerCase().replace(/ /g, '-')}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
              attivo
                ? 'bg-orange-500 border-orange-500 text-white'
                : 'bg-slate-800 lg:bg-white border-slate-600 lg:border-gray-300 text-slate-300 lg:text-gray-600 hover:border-orange-400'
            }`}
          >
            {attivo ? '✓ ' : '+ '}{a}{attivo && isAuto ? ' (auto)' : ''}
          </button>
        );
      })}
    </div>
    <p className="text-[10px] text-orange-400/70 lg:text-orange-600">Rilevati automaticamente dagli ingredienti. Tocca per aggiungere o rimuovere.</p>
  </div>
);

// ============ ETICHETTE CONSERVAZIONE SECTION (NUOVO FLUSSO) ============
const SiriEtichetteCard = () => {
  const [aperta, setAperta] = useState(false);
  const tenantDb = localStorage.getItem('tenant_db') || '';
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const urlEndpoint = `${baseUrl}/api/haccp/etichetta-vocale?tenant=${tenantDb}`;

  const copiaUrl = () => {
    navigator.clipboard.writeText(urlEndpoint);
    toast.success('URL copiato!');
  };

  return (
    <div className="mb-4 rounded-xl border border-purple-700/40 lg:border-purple-200 bg-purple-900/20 lg:bg-purple-50 overflow-hidden" data-testid="siri-etichette-card">
      <button
        onClick={() => setAperta(!aperta)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="siri-card-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-purple-300 lg:text-purple-800">
          <Mic className="h-4 w-4" /> Crea etichette con "Ehi Siri"
        </span>
        {aperta ? <ChevronUp className="h-4 w-4 text-purple-400" /> : <ChevronDown className="h-4 w-4 text-purple-400" />}
      </button>
      {aperta && (
        <div className="px-4 pb-4 text-xs text-slate-300 lg:text-gray-700 space-y-2">
          <p className="font-medium text-purple-300 lg:text-purple-700">Configura una volta sola sull'iPhone (app Comandi):</p>
          <ol className="list-decimal ml-4 space-y-1">
            <li>Apri l'app <b>Comandi</b> → tocca <b>+</b> per un nuovo comando</li>
            <li>Aggiungi azione <b>"Detta testo"</b> (lingua: Italiano)</li>
            <li>Aggiungi azione <b>"Ottieni contenuto di URL"</b>: incolla l'URL qui sotto, Metodo <b>POST</b>, Corpo richiesta <b>JSON</b>, aggiungi campo <b>testo</b> = variabile <i>Testo dettato</i></li>
            <li>Aggiungi azione <b>"Ottieni valore dizionario"</b> → chiave <b>messaggio</b> + azione <b>"Pronuncia testo"</b>: Siri ti dice se mancano dati (metodo, quantità, ricetta, allergeni)</li>
            <li>Aggiungi azione <b>"Ottieni valore dizionario"</b> → chiave <b>url_stampa</b> in <i>Contenuto URL</i></li>
            <li>Aggiungi azione <b>"Apri URL"</b> → si apre l'etichetta PDF pronta da stampare</li>
            <li>Rinomina il comando <b>"Nuova etichetta"</b> → poi basta dire <b>"Ehi Siri, nuova etichetta"</b></li>
          </ol>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 px-2 py-1.5 rounded bg-black/40 lg:bg-white border border-purple-700/40 lg:border-purple-200 text-purple-300 lg:text-purple-700 break-all text-[10px]" data-testid="siri-url-endpoint">{urlEndpoint}</code>
            <button onClick={copiaUrl} className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white" title="Copia URL" data-testid="siri-copia-url">
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-slate-400 lg:text-gray-500">Detta prodotto, metodo e quantità: es. <i>"3 porzioni di ragù abbattuto"</i> (90 gg, -18°), <i>"salsa verde"</i> (frigo 3 gg), <i>"2 kg di ragù congelato"</i> (180 gg), <i>"tonno sottovuoto"</i> (21 gg). Se esiste una ricetta con quel nome viene importata (ingredienti e allergeni inclusi), altrimenti l'etichetta si crea da zero e Siri ti avvisa di cosa manca.</p>
        </div>
      )}
    </div>
  );
};

const EtichetteConservazioneSection = () => {
  // Helper per convertire data da YYYY-MM-DD a DD/MM/YYYY
  const formatDateIta = (dateStr) => {
    if (!dateStr) return '';
    // Se è già in formato italiano, ritorna così
    if (dateStr.includes('/')) return dateStr;
    // Altrimenti converte da YYYY-MM-DD
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };
  
  // Helper per convertire data da DD/MM/YYYY a YYYY-MM-DD (per input date HTML)
  const formatDateInput = (dateStr) => {
    if (!dateStr) return '';
    // Se è già in formato input, ritorna così
    if (dateStr.includes('-') && !dateStr.includes('/')) return dateStr;
    // Altrimenti converte da DD/MM/YYYY
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };
  
  // Ottiene data odierna in formato YYYY-MM-DD per input HTML
  const getTodayInput = () => new Date().toISOString().split('T')[0];
  
  // Ottiene data odierna in formato DD/MM/YYYY
  const getTodayIta = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const [lotti, setLotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('attive'); // 'attive' o 'scadute'
  const [selAttive, setSelAttive] = useState([]);
  const [selScadute, setSelScadute] = useState([]);
  const [azioneMultipla, setAzioneMultipla] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [ricette, setRicette] = useState([]);
  const [selectedRicetta, setSelectedRicetta] = useState(null);
  const [ricettaSearch, setRicettaSearch] = useState('');
  
  // Step del flusso import: 1=selezione ricetta, 2=preparazione lotto + ingredienti, 3=conservazione, 4=riepilogo
  const [importStep, setImportStep] = useState(1);
  const [numeroLotto, setNumeroLotto] = useState('');
  
  // Form preparazione lotto
  const [lottoForm, setLottoForm] = useState({
    data_produzione: getTodayInput(),
    quantita: 1,
    unita_misura: 'porzioni',
    note: '',
    tipo_scadenza: '', // Nessuna preselezione - l'utente deve scegliere
    scadenza_custom: '' // Per shelf life personalizzata
  });
  
  // Ingredienti della ricetta da riconciliare
  const [ingredientiRiconciliati, setIngredientiRiconciliati] = useState([]);
  const [ingredienteSelezionato, setIngredienteSelezionato] = useState(null);
  const [showRiconciliaModal, setShowRiconciliaModal] = useState(false);
  const [ddtSuggestions, setDdtSuggestions] = useState([]);
  const [ddtSuggestionsGruppi, setDdtSuggestionsGruppi] = useState([]);
  const [loadingDdt, setLoadingDdt] = useState(false);
  
  // Prodotti interni (etichette conservazione attive) per riconciliazione
  const [prodottiInterni, setProdottiInterni] = useState([]);
  const [loadingProdottiInterni, setLoadingProdottiInterni] = useState(false);
  const [riconciliaTab, setRiconciliaTab] = useState('ddt'); // 'ddt' o 'interni'
  
  // Conservazione (ex Abbattimento)
  const [conservazione, setConservazione] = useState({
    necessaria: false,
    metodo: '', // abbattimento, sottovuoto, sottosale, sottolio, marinatura, affumicatura, congelamento, essiccazione
    inizio_data: getTodayInput(),
    inizio_ora: '',
    fine_data: getTodayInput(),
    fine_ora: '',
    temperatura: -18,
    eviscerato: false,
    presenza_vermi: false,
    note_conservazione: ''
  });
  
  // Metodi di conservazione disponibili
  const METODI_CONSERVAZIONE = [
    { id: 'abbattimento', label: 'Abbattimento', icon: '❄️', giorni_extra: 90, temp: -18 },
    { id: 'sottovuoto', label: 'Sottovuoto', icon: '📦', giorni_extra: 21, temp: 4 },
    { id: 'congelamento', label: 'Congelamento', icon: '🧊', giorni_extra: 180, temp: -18 },
    { id: 'sottosale', label: 'Sotto Sale', icon: '🧂', giorni_extra: 60, temp: null },
    { id: 'sottolio', label: "Sott'Olio", icon: '🫒', giorni_extra: 180, temp: null },
    { id: 'sottaceto', label: "Sott'Aceto", icon: '🥒', giorni_extra: 365, temp: null },
    { id: 'marinatura', label: 'Marinatura', icon: '🍋', giorni_extra: 7, temp: 4 },
    { id: 'affumicatura', label: 'Affumicatura', icon: '🔥', giorni_extra: 14, temp: 4 },
    { id: 'essiccazione', label: 'Essiccazione', icon: '☀️', giorni_extra: 180, temp: null },
    { id: 'pastorizzazione', label: 'Pastorizzazione', icon: '🔬', giorni_extra: 30, temp: 4 },
  ];
  
  // Frazionamento ricetta - ora con quantità assolute e scheda conservazione per ogni frazione
  const [frazionamento, setFrazionamento] = useState({
    abilitato: false,
    frazioni: [
      { 
        quantita: 0, 
        conservazione: '', 
        giorni_scadenza: 3,
        // Dati scheda conservazione specifici per questa frazione
        inizio_data: getTodayInput(),
        inizio_ora: '',
        fine_data: getTodayInput(),
        fine_ora: '',
        temperatura: -18,
        eviscerato: false,
        presenza_vermi: false,
        note_conservazione: ''
      }
    ]
  });
  
  // Salvataggio
  const [saving, setSaving] = useState(false);
  
  // AI Recipe Search
  const [cercandoRicettaAI, setCercandoRicettaAI] = useState(false);
  
  // Modifica ingrediente riconciliato
  const [editingIngredienteIdx, setEditingIngredienteIdx] = useState(null);
  const [ddtSearchForEdit, setDdtSearchForEdit] = useState([]);
  const [ddtSearchForEditGruppi, setDdtSearchForEditGruppi] = useState([]);
  const [searchingDdtForEdit, setSearchingDdtForEdit] = useState(false);
  
  // Nuovi stati per QR, Dettaglio, Allergeni
  const [showQrModal, setShowQrModal] = useState(false);
  const [showDettaglioModal, setShowDettaglioModal] = useState(false);
  const [selectedLotto, setSelectedLotto] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [analizzandoAllergeni, setAnalizzandoAllergeni] = useState(false);
  
  // Stati per modifica lotto
  const [editMode, setEditMode] = useState(false);
  const [editingLotto, setEditingLotto] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [puoModificare, setPuoModificare] = useState(false);
  
  // Stati per storico DDT ingrediente
  const [showStoricoDdtModal, setShowStoricoDdtModal] = useState(false);
  const [storicoDdtIngrediente, setStoricoDdtIngrediente] = useState(null);
  const [storicoDdtResults, setStoricoDdtResults] = useState([]);
  const [loadingStoricoDdt, setLoadingStoricoDdt] = useState(false);
  const [storicoDdtIngredienteIdx, setStoricoDdtIngredienteIdx] = useState(null);
  
  // Stati per aggiunta/rimozione ingredienti
  const [showAddIngredienteModal, setShowAddIngredienteModal] = useState(false);
  const [nuovoIngredienteNome, setNuovoIngredienteNome] = useState('');
  const [nuovoIngredienteDdtResults, setNuovoIngredienteDdtResults] = useState([]);
  const [nuovoIngredienteDdtGruppi, setNuovoIngredienteDdtGruppi] = useState([]);
  const [searchingNewIngrediente, setSearchingNewIngrediente] = useState(false);
  const [addingToDettaglio, setAddingToDettaglio] = useState(false); // true se stiamo aggiungendo a un lotto esistente
  const [editingIngredienteId, setEditingIngredienteId] = useState(null); // ID ingrediente in modifica
  const [editingIngredienteNome, setEditingIngredienteNome] = useState(''); // Nome temporaneo in modifica
  
  // NUOVO: Stati per "Crea Ricetta" manualmente
  const [showCreaRicettaModal, setShowCreaRicettaModal] = useState(false);
  const [allergeniAuto, setAllergeniAuto] = useState([]);
  const [allergeniSelezionati, setAllergeniSelezionati] = useState([]);
  const [allergeniTouched, setAllergeniTouched] = useState(false);
  const [creaRicettaStep, setCreaRicettaStep] = useState(1); // 1=nome+ingredienti, 2=preparazione, 3=conservazione
  const [nuovaRicetta, setNuovaRicetta] = useState({
    nome: '',
    ingredienti: []
  });
  const [nuovoIngredienteCrea, setNuovoIngredienteCrea] = useState({
    nome: '',
    quantita: '',
    unita_misura: 'g',
    lotto: '',
    scadenza: '',
    prodotto_ddt: null // riferimento al prodotto DDT selezionato
  });
  const [ddtSearchResults, setDdtSearchResults] = useState([]);
  const [ddtSearchResultsGruppi, setDdtSearchResultsGruppi] = useState([]);
  const [searchingDdt, setSearchingDdt] = useState(false);
  
  // Stati per impostazioni etichetta (logo)
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [logoSettings, setLogoSettings] = useState({
    logo_base64: null,
    logo_filename: null,
    logo_posizione: 'basso-destra',
    logo_size: 10
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadLotti();
    loadRicette();
    loadLogoSettings();
    caricaPermessoModifica().then(setPuoModificare);
  }, []);

  // Helper per ottenere headers con autenticazione e tenant
  const getAuthHeaders = () => {
    const token = localStorage.getItem('tenant_token') || localStorage.getItem('token');
    let tenantDb = localStorage.getItem('tenant_db');
    if (!tenantDb) {
      const userStr = localStorage.getItem('tenant_user') || localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      tenantDb = user.tenants && user.tenants[0] ? user.tenants[0].database_name : null;
    }
    if (!tenantDb) {
      console.warn('Nessun tenant configurato');
      return null;
    }
    
    const headers = { 
      'Authorization': `Bearer ${token}`,
      'X-Tenant-DB': tenantDb.replace('tenant_', '')
    };
    return headers;
  };

  const loadLotti = async () => {
    try {
      // Usa lo stesso approccio delle altre funzioni per consistenza
      const token = localStorage.getItem('tenant_token') || localStorage.getItem('token');
      let tenantDb = localStorage.getItem('tenant_db');
      if (!tenantDb) {
        const userStr = localStorage.getItem('tenant_user') || localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        tenantDb = user.tenants && user.tenants[0] ? user.tenants[0].database_name : null;
      }
      if (!tenantDb) {
        console.warn('Nessun tenant configurato');
        setLotti([]);
        setLoading(false);
        return;
      }
      
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'X-Tenant-DB': tenantDb.replace('tenant_', '')
      };
      
      const res = await axios.get(`${API}/gestione/haccp/lotti-produzione-avanzati`, {
        headers
      });
      setLotti(res.data.lotti || []);
    } catch (error) {
      console.error('Errore caricamento lotti:', error);
      
    } finally {
      setLoading(false);
    }
  };

  const loadRicette = async () => {
    try {
      // Usa lo stesso approccio delle altre funzioni per consistenza
      const token = localStorage.getItem('tenant_token') || localStorage.getItem('token');
      // Prima prova localStorage.tenant_db, poi estrai da user.tenants
      let tenantDb = localStorage.getItem('tenant_db');
      if (!tenantDb) {
        const userStr = localStorage.getItem('tenant_user') || localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        tenantDb = user.tenants && user.tenants[0] ? user.tenants[0].database_name : null;
      }
      if (!tenantDb) {
        console.warn('Nessun tenant configurato per ricette');
        setRicette([]);
        return;
      }
      
      console.log(`🔍 Loading ricette - tenantDb: ${tenantDb}`);
      
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'X-Tenant-DB': tenantDb.replace('tenant_', '')
      };
      
      const res = await axios.get(`${API}/gestione/haccp/ricette`, {
        headers
      });
      
      setRicette(res.data.ricette || []);
      console.log(`✅ Ricette caricate: ${res.data.ricette?.length || 0}`);
    } catch (error) {
      console.error('❌ Errore caricamento ricette:', error);
      console.error('❌ Error response:', error.response?.data);
      setRicette([]);
    }
  };

  // Carica impostazioni etichetta
  const loadLogoSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/gestione/haccp/impostazioni-etichetta`, {
        headers: getApiHeaders()
      });
      if (res.data.impostazioni) {
        setLogoSettings(res.data.impostazioni);
        if (res.data.impostazioni.logo_base64) {
          setLogoPreview(`data:image/png;base64,${res.data.impostazioni.logo_base64}`);
        }
      }
    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
    }
  };

  // Gestione upload logo
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      toast.error('Formato non supportato. Usa JPG o PNG.');
      return;
    }
    
    if (file.size > 500000) { // 500KB max
      toast.error('Immagine troppo grande. Max 500KB.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      setLogoSettings(prev => ({
        ...prev,
        logo_base64: base64,
        logo_filename: file.name
      }));
      setLogoPreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Salva impostazioni logo
  const saveLogoSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/gestione/haccp/impostazioni-etichetta/logo`, logoSettings, {
        headers: getApiHeaders()
      });
      toast.success('Impostazioni salvate!');
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingSettings(false);
    }
  };

  // Rimuovi logo
  const removeLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/gestione/haccp/impostazioni-etichetta/logo`, {
        headers: getApiHeaders()
      });
      setLogoSettings(prev => ({ ...prev, logo_base64: null, logo_filename: null }));
      setLogoPreview(null);
      toast.success('Logo rimosso');
    } catch (error) {
      toast.error('Errore rimozione logo');
    }
  };

  // Scarica anteprima etichetta
  const downloadAnteprimaEtichetta = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/gestione/haccp/anteprima-etichetta`, {
        headers: getApiHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'anteprima_etichetta.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Errore generazione anteprima');
    }
  };

  // Step 1: Seleziona ricetta e vai a step 2
  const handleSelectRicetta = async (ricetta) => {
    setSelectedRicetta(ricetta);
    
    // Genera numero lotto
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/gestione/haccp/genera-numero-lotto`, {
        headers: getApiHeaders()
      });
      setNumeroLotto(res.data.numero_lotto);
    } catch (error) {
      console.error('Errore generazione numero lotto:', error);
      setNumeroLotto('---/----');
    }
    
    // Prepara ingredienti da riconciliare
    const ingredienti = (ricetta.ingredienti || []).map((ing, idx) => ({
      id: idx,
      nome_ingrediente: ing.nome,
      riconciliato: false,
      nome_prodotto_ddt: null,
      fornitore: null,
      numero_lotto: null,
      scadenza_prodotto: null,
      ddt_id: null,
      ddt_numero: null,
      data_ddt: null
    }));
    setIngredientiRiconciliati(ingredienti);
    
    // Auto-riconcilia tutti gli ingredienti in background
    autoRiconciliaIngredienti(ingredienti);
    
    setImportStep(2);
  };
  
  // Auto-riconcilia tutti gli ingredienti cercando nei DDT
  const autoRiconciliaIngredienti = async (ingredienti) => {
    const token = localStorage.getItem('token');
    const tenantDb = localStorage.getItem('tenant_db');
    
    if (!tenantDb) {
      console.warn('Nessun tenant_db trovato nel localStorage');
      return ingredienti;
    }
    
    const risultati = [...ingredienti];
    
    for (let i = 0; i < ingredienti.length; i++) {
      const ing = ingredienti[i];
      try {
        const res = await axios.get(`${API}/gestione/haccp/cerca-ingrediente-ddt`, {
          params: { nome: ing.nome_ingrediente, limit: 5 },
          headers: { 
            'Authorization': `Bearer ${token}`,
            'X-Tenant-DB': tenantDb
          }
        });
        
        const matches = res.data.risultati || [];
        // Prendi il primo risultato con lotto (se esiste)
        const bestMatch = matches.find(m => m.numero_lotto) || matches[0];
        
        if (bestMatch) {
          risultati[i] = {
            ...ing,
            riconciliato: true,
            nome_prodotto_ddt: bestMatch.nome_prodotto || null,
            fornitore: bestMatch.fornitore || null,
            numero_lotto: bestMatch.numero_lotto ? String(bestMatch.numero_lotto) : null,
            scadenza_prodotto: bestMatch.scadenza || null,
            ddt_id: bestMatch.ddt_id || null,
            ddt_numero: bestMatch.ddt_numero != null ? String(bestMatch.ddt_numero) : null,
            data_ddt: bestMatch.data_ddt || null
          };
        }
      } catch (error) {
        console.error(`Errore auto-riconciliazione ${ing.nome_ingrediente}:`, error);
      }
    }
    
    setIngredientiRiconciliati(risultati);
    
    const riconciliati = risultati.filter(r => r.riconciliato).length;
    if (riconciliati > 0) {
      toast.success(`✓ Auto-riconciliati ${riconciliati}/${risultati.length} ingredienti`);
    }
  };

  // Cerca prodotto nei DDT
  const cercaProdottoNeiDDT = async (nomeIngrediente) => {
    setLoadingDdt(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/gestione/haccp/cerca-ingrediente-ddt`, {
        params: { nome: nomeIngrediente, limit: 10 },
        headers: getApiHeaders()
      });
      setDdtSuggestions(res.data.risultati || []);
      setDdtSuggestionsGruppi(res.data.gruppi || []);
    } catch (error) {
      console.error('Errore ricerca DDT:', error);
      setDdtSuggestions([]);
      setDdtSuggestionsGruppi([]);
    } finally {
      setLoadingDdt(false);
    }
  };

  // Cerca prodotti interni (lotti di produzione attivi = preparazioni interne)
  const cercaProdottiInterni = async (nomeIngrediente) => {
    setLoadingProdottiInterni(true);
    try {
      // Cerca nei LOTTI di produzione avanzati (le preparazioni interne reali)
      const res = await axios.get(`${API}/gestione/haccp/lotti-produzione-avanzati`, {
        headers: getApiHeaders()
      });
      const lotti = res.data.lotti || [];
      console.log('Lotti totali:', lotti.length, '- Ricerca per:', nomeIngrediente);
      
      // Filtra: solo lotti attivi (non scaduti) che matchano il nome
      const oggi = new Date();
      const searchLower = (nomeIngrediente || '').toLowerCase();
      
      const attivi = lotti.filter(l => {
        // Controlla scadenza
        if (l.data_scadenza) {
          const parts = l.data_scadenza.split('/');
          if (parts.length === 3) {
            const scadenza = new Date(parts[2], parts[1] - 1, parts[0]);
            if (scadenza < oggi) return false;
          }
        }
        // Match per nome ricetta o nome lotto
        const nome = (l.ricetta_nome || l.nome || '').toLowerCase();
        return nome.includes(searchLower);
      });
      
      // Mappa al formato atteso dal modal
      const prodotti = attivi.map(l => ({
        id: l.id,
        nome: l.ricetta_nome || l.nome || 'Lotto',
        numero_lotto: l.numero_lotto,
        data_produzione: l.data_produzione,
        data_consumo: l.data_scadenza,
        quantita: l.quantita,
        unita_misura: l.unita_misura,
        stato: l.stato
      }));
      
      setProdottiInterni(prodotti);
    } catch (error) {
      console.error('Errore ricerca prodotti interni:', error);
      setProdottiInterni([]);
    } finally {
      setLoadingProdottiInterni(false);
    }
  };

  // Apri modal riconciliazione per un ingrediente
  const openRiconciliaModal = (ingrediente) => {
    setIngredienteSelezionato(ingrediente);
    setShowRiconciliaModal(true);
    setRiconciliaTab('ddt');
    cercaProdottoNeiDDT(ingrediente.nome_ingrediente);
    cercaProdottiInterni(ingrediente.nome_ingrediente);
  };

  // Conferma riconciliazione con DDT
  const confermaRiconciliazione = (ddtItem) => {
    setIngredientiRiconciliati(prev => prev.map(ing => 
      ing.id === ingredienteSelezionato.id 
        ? {
            ...ing,
            riconciliato: true,
            tipo_fonte: 'ddt',
            nome_prodotto_ddt: ddtItem.nome_prodotto || null,
            fornitore: ddtItem.fornitore || null,
            numero_lotto: ddtItem.numero_lotto ? String(ddtItem.numero_lotto) : null,
            scadenza_prodotto: ddtItem.scadenza || null,
            ddt_id: ddtItem.ddt_id || null,
            ddt_numero: ddtItem.ddt_numero != null ? String(ddtItem.ddt_numero) : null,
            data_ddt: ddtItem.data_ddt || null,
            // Campi prodotto interno vuoti
            prodotto_interno_id: null,
            prodotto_interno_nome: null,
            prodotto_interno_lotto: null
          }
        : ing
    ));
    setShowRiconciliaModal(false);
    setIngredienteSelezionato(null);
    toast.success('Ingrediente riconciliato con DDT!');
  };

  // Conferma riconciliazione con prodotto INTERNO (etichetta conservazione)
  const confermaRiconciliazioneInterna = (etichetta) => {
    setIngredientiRiconciliati(prev => prev.map(ing => 
      ing.id === ingredienteSelezionato.id 
        ? {
            ...ing,
            riconciliato: true,
            tipo_fonte: 'interno',
            nome_prodotto_ddt: etichetta.nome || null,
            fornitore: 'Produzione Interna',
            numero_lotto: etichetta.numero_lotto || etichetta.id || null,
            scadenza_prodotto: etichetta.data_consumo || null,
            ddt_id: null,
            ddt_numero: null,
            data_ddt: etichetta.data_produzione || null,
            // Campi specifici prodotto interno
            prodotto_interno_id: etichetta.id,
            prodotto_interno_nome: etichetta.nome,
            prodotto_interno_lotto: etichetta.numero_lotto || etichetta.id
          }
        : ing
    ));
    setShowRiconciliaModal(false);
    setIngredienteSelezionato(null);
    toast.success('Ingrediente riconciliato con Prodotto Interno!');
  };

  // Salva lotto produzione (supporta frazionamento con lotti multipli)
  const salvaLottoProduzione = async (stampare = true) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!selectedRicetta?.id || !selectedRicetta?.nome) {
        toast.error('Errore: ricetta non selezionata correttamente');
        setSaving(false);
        return;
      }
      
      // Formatta data produzione
      const dataProd = new Date(lottoForm.data_produzione);
      if (isNaN(dataProd.getTime())) {
        toast.error('Errore: data produzione non valida');
        setSaving(false);
        return;
      }
      const dataProdStr = `${String(dataProd.getDate()).padStart(2, '0')}/${String(dataProd.getMonth() + 1).padStart(2, '0')}/${dataProd.getFullYear()}`;
      
      // Prepara ingredienti con tutti i campi come stringhe dove richiesto
      const ingredientiPayload = ingredientiRiconciliati.map(ing => ({
        nome_ingrediente: String(ing.nome_ingrediente || ''),
        nome_prodotto_ddt: ing.nome_prodotto_ddt || null,
        fornitore: ing.fornitore || null,
        numero_lotto: ing.numero_lotto ? String(ing.numero_lotto) : null,
        scadenza_prodotto: ing.scadenza_prodotto || null,
        ddt_id: ing.ddt_id || null,
        ddt_numero: ing.ddt_numero != null ? String(ing.ddt_numero) : null,
        data_ddt: ing.data_ddt || null,
        quantita: ing.quantita ? parseFloat(ing.quantita) : null,
        unita_misura: ing.unita_misura || null,
        // Campi per distinguere fonte DDT vs Produzione Interna
        tipo_fonte: ing.tipo_fonte || 'ddt',
        prodotto_interno_id: ing.prodotto_interno_id || null,
        prodotto_interno_nome: ing.prodotto_interno_nome || null,
        prodotto_interno_lotto: ing.prodotto_interno_lotto || null
      }));
      
      // ==========================================
      // GESTIONE FRAZIONAMENTO - Lotti multipli
      // ==========================================
      if (frazionamento.abilitato && frazionamento.frazioni.length > 0) {
        // Valida che le quantità siano corrette
        const totaleAssegnato = frazionamento.frazioni.reduce((sum, f) => sum + (parseFloat(f.quantita) || 0), 0);
        const quantitaTotale = parseFloat(lottoForm.quantita) || 0;
        
        if (totaleAssegnato !== quantitaTotale) {
          toast.error(`Le quantità delle frazioni (${totaleAssegnato}) non corrispondono al totale (${quantitaTotale} ${lottoForm.unita_misura})`);
          setSaving(false);
          return;
        }
        
        // Valida che ogni frazione abbia una quantità > 0
        const frazioniValide = frazionamento.frazioni.filter(f => (parseFloat(f.quantita) || 0) > 0);
        if (frazioniValide.length === 0) {
          toast.error('Inserisci almeno una frazione con quantità maggiore di zero');
          setSaving(false);
          return;
        }
        
        // Crea un lotto per ogni frazione
        const lottiCreati = [];
        let errori = [];
        
        for (let i = 0; i < frazioniValide.length; i++) {
          const fraz = frazioniValide[i];
          
          // Calcola data scadenza per questa frazione
          const giorniScadenza = fraz.giorni_scadenza || 3;
          const dataScad = new Date(dataProd);
          dataScad.setDate(dataScad.getDate() + giorniScadenza);
          const dataScadenzaStr = `${String(dataScad.getDate()).padStart(2, '0')}/${String(dataScad.getMonth() + 1).padStart(2, '0')}/${dataScad.getFullYear()}`;
          
          // Verifica se questo metodo richiede la scheda conservazione
          const haConservazione = fraz.conservazione && fraz.conservazione !== '';
          
          const payloadFrazione = {
            ricetta_id: selectedRicetta.id,
            ricetta_nome: `${selectedRicetta.nome}${frazioniValide.length > 1 ? ` (${i + 1}/${frazioniValide.length})` : ''}`,
            data_produzione: dataProdStr,
            data_scadenza: dataScadenzaStr,
            giorni_scadenza: giorniScadenza,
            quantita: parseFloat(fraz.quantita),
            unita_misura: lottoForm.unita_misura || 'porzioni',
            ingredienti_riconciliati: ingredientiPayload,
            // Scheda conservazione per questa frazione
            abbattimento: haConservazione,
            abbattimento_inizio_data: haConservazione ? formatDateIta(fraz.inizio_data) : null,
            abbattimento_inizio_ora: haConservazione ? fraz.inizio_ora : null,
            abbattimento_fine_data: haConservazione ? formatDateIta(fraz.fine_data) : null,
            abbattimento_fine_ora: haConservazione ? fraz.fine_ora : null,
            temperatura_abbattimento: haConservazione ? parseFloat(fraz.temperatura) : null,
            eviscerato: haConservazione ? fraz.eviscerato : null,
            presenza_vermi: haConservazione ? fraz.presenza_vermi : null,
            metodo_conservazione: haConservazione ? fraz.conservazione : null,
            note_conservazione: haConservazione ? fraz.note_conservazione : null,
            note: `Frazione ${i + 1}/${frazioniValide.length}: ${fraz.quantita} ${lottoForm.unita_misura} - ${fraz.conservazione ? METODI_CONSERVAZIONE.find(m => m.id === fraz.conservazione)?.label : 'Fresco'}`,
            ...(allergeniTouched ? { allergeni_manuali: allergeniSelezionati } : {})
          };
          
          console.log(`Payload frazione ${i + 1}:`, payloadFrazione);
          
          try {
            const res = await axios.post(`${API}/gestione/haccp/lotti-produzione-avanzati`, payloadFrazione, {
              headers: getApiHeaders()
            });
            
            if (res.data.success) {
              lottiCreati.push(res.data.lotto);
            } else {
              errori.push(`Frazione ${i + 1}: ${res.data.detail || 'Errore'}`);
            }
          } catch (err) {
            errori.push(`Frazione ${i + 1}: ${err.response?.data?.detail || err.message}`);
          }
        }
        
        // Risultato finale
        if (lottiCreati.length > 0) {
          toast.success(`✅ Creati ${lottiCreati.length} lotti di produzione!`);
          
          // Stampa le etichette se richiesto
          if (stampare) {
            for (const lotto of lottiCreati) {
              await downloadPdfEtichetta(lotto.id);
            }
          }
          
          closeImportModal();
          loadLotti();
        }
        
        if (errori.length > 0) {
          toast.error(`Errori: ${errori.join('; ')}`);
        }
        
        return;
      }
      
      // ==========================================
      // LOTTO SINGOLO (senza frazionamento)
      // ==========================================
      // Calcola data scadenza in base al tipo selezionato o al metodo di conservazione
      let dataScadenzaStr = '';
      let giorniScadenzaEffettivi = null;
      
      if (lottoForm.tipo_scadenza === 'custom' && lottoForm.scadenza_custom) {
        // Shelf life custom
        const scadCustom = new Date(lottoForm.scadenza_custom);
        dataScadenzaStr = `${String(scadCustom.getDate()).padStart(2, '0')}/${String(scadCustom.getMonth() + 1).padStart(2, '0')}/${scadCustom.getFullYear()}`;
        giorniScadenzaEffettivi = null;
      } else if (lottoForm.tipo_scadenza && lottoForm.tipo_scadenza !== '') {
        // Giorni espliciti selezionati (30, 60, 90, 120)
        giorniScadenzaEffettivi = parseInt(lottoForm.tipo_scadenza);
        const dataScad = new Date(dataProd);
        dataScad.setDate(dataScad.getDate() + giorniScadenzaEffettivi);
        dataScadenzaStr = `${String(dataScad.getDate()).padStart(2, '0')}/${String(dataScad.getMonth() + 1).padStart(2, '0')}/${dataScad.getFullYear()}`;
      } else {
        // Nessuna scadenza selezionata - usa il metodo di conservazione
        if (conservazione.necessaria && conservazione.metodo) {
          const metodo = METODI_CONSERVAZIONE.find(m => m.id === conservazione.metodo);
          giorniScadenzaEffettivi = metodo ? metodo.giorni_extra : 3;
        } else {
          // Senza conservazione = 3 giorni
          giorniScadenzaEffettivi = 3;
        }
        const dataScad = new Date(dataProd);
        dataScad.setDate(dataScad.getDate() + giorniScadenzaEffettivi);
        dataScadenzaStr = `${String(dataScad.getDate()).padStart(2, '0')}/${String(dataScad.getMonth() + 1).padStart(2, '0')}/${dataScad.getFullYear()}`;
      }
      
      const payload = {
        ricetta_id: selectedRicetta.id,
        ricetta_nome: selectedRicetta.nome,
        data_produzione: dataProdStr,
        data_scadenza: dataScadenzaStr,
        giorni_scadenza: giorniScadenzaEffettivi,
        quantita: parseFloat(lottoForm.quantita) || 1,
        unita_misura: lottoForm.unita_misura || 'porzioni',
        ingredienti_riconciliati: ingredientiPayload,
        abbattimento: conservazione.necessaria,
        abbattimento_inizio_data: conservazione.necessaria ? formatDateIta(conservazione.inizio_data) : null,
        abbattimento_inizio_ora: conservazione.necessaria ? conservazione.inizio_ora : null,
        abbattimento_fine_data: conservazione.necessaria ? formatDateIta(conservazione.fine_data) : null,
        abbattimento_fine_ora: conservazione.necessaria ? conservazione.fine_ora : null,
        temperatura_abbattimento: conservazione.necessaria ? parseFloat(conservazione.temperatura) : null,
        eviscerato: conservazione.necessaria ? conservazione.eviscerato : null,
        presenza_vermi: conservazione.necessaria ? conservazione.presenza_vermi : null,
        metodo_conservazione: conservazione.necessaria ? conservazione.metodo : null,
        note_conservazione: conservazione.necessaria ? conservazione.note_conservazione : null,
        note: lottoForm.note || '',
        ...(allergeniTouched ? { allergeni_manuali: allergeniSelezionati } : {})
      };

      console.log('Payload salvataggio lotto:', payload);

      const res = await axios.post(`${API}/gestione/haccp/lotti-produzione-avanzati`, payload, {
        headers: getApiHeaders()
      });

      if (res.data.success) {
        toast.success('Lotto di produzione creato!');
        // Scarica PDF solo se richiesto
        if (stampare) {
          downloadPdfEtichetta(res.data.lotto.id);
        }
        closeImportModal();
        loadLotti();
      } else {
        const errDetail = res.data.detail;
        const errMsg = typeof errDetail === 'string' ? errDetail : 'Errore nel salvataggio';
        toast.error(errMsg);
      }
    } catch (error) {
      console.error('Errore salvataggio lotto:', error);
      // Gestisci errori di validazione Pydantic (array di oggetti)
      let errorMsg = 'Errore nel salvataggio del lotto';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // Errore di validazione Pydantic
          errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMsg = detail.msg;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Download PDF etichetta (apre in nuova finestra per stampare)
  const downloadPdfEtichetta = async (lottoId) => {
    const tenantDb = localStorage.getItem('tenant_db');
    
    if (!tenantDb) {
      toast.error('Errore: tenant non configurato');
      return;
    }
    
    try {
      // Scarica il PDF come blob
      const url = `${API}/haccp/genera-etichetta-pdf-file/${lottoId}?tenant=${tenantDb}`;
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Crea URL blob
      const blobUrl = URL.createObjectURL(response.data);
      
      // Crea pagina HTML con embed PDF e pulsante stampa
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Stampa Etichetta</title>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: Arial, sans-serif;
                background: #f5f5f5;
                min-height: 100vh;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 20px;
              }
              .btn-container {
                margin-bottom: 20px;
              }
              .print-btn {
                background: #4CAF50;
                color: white;
                border: none;
                padding: 18px 50px;
                font-size: 20px;
                font-weight: bold;
                border-radius: 10px;
                cursor: pointer;
                box-shadow: 0 4px 15px rgba(0,0,0,0.2);
              }
              .print-btn:active { background: #45a049; }
              .pdf-container {
                width: 100%;
                max-width: 400px;
                background: white;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              }
              embed, iframe {
                width: 100%;
                height: 200px;
                border: none;
              }
              .instructions {
                margin-top: 20px;
                text-align: center;
                color: #666;
                font-size: 14px;
              }
            </style>
          </head>
          <body>
            <div class="btn-container">
              <button class="print-btn" onclick="printPdf()">🖨️ STAMPA ETICHETTA</button>
            </div>
            <div class="pdf-container">
              <embed src="${blobUrl}" type="application/pdf" />
            </div>
            <p class="instructions">
              Se il pulsante non funziona, usa il menu ⋮ del browser → Stampa
            </p>
            <script>
              function printPdf() {
                window.print();
              }
            </script>
          </body>
          </html>
        `);
        printWindow.document.close();
        toast.success('Clicca STAMPA ETICHETTA');
      } else {
        // Fallback: scarica il file
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'etichetta.pdf';
        link.click();
        toast.info('PDF scaricato. Aprilo e stampa.');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore generazione etichetta');
    }
  };

  // NUOVO: Genera numero lotto dall'API
  const fetchNumeroLotto = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/gestione/haccp/genera-numero-lotto`, {
        headers: getApiHeaders()
      });
      setNumeroLotto(res.data.numero_lotto);
    } catch (error) {
      console.error('Errore generazione numero lotto:', error);
      // Fallback con formato semplice
      const now = new Date();
      setNumeroLotto(`${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}/${now.getFullYear()}`);
    }
  };

  // NUOVO: Apri modal Crea Ricetta
  const openCreaRicettaModal = async () => {
    setShowAddModal(false);
    setShowCreaRicettaModal(true);
    setCreaRicettaStep(1);
    setNuovaRicetta({ nome: '', ingredienti: [] });
    setNuovoIngredienteCrea({
      nome: '',
      quantita: '',
      unita_misura: 'g',
      lotto: '',
      scadenza: '',
      prodotto_ddt: null
    });
    setLottoForm({
      data_produzione: getTodayInput(),
      quantita: 1,
      unita_misura: 'porzioni',
      note: '',
      tipo_scadenza: '', // Nessuna preselezione
      scadenza_custom: ''
    });
    setConservazione({
      necessaria: false,
      metodo: '',
      inizio_data: getTodayInput(),
      inizio_ora: '',
      fine_data: getTodayInput(),
      fine_ora: '',
      temperatura: -18,
      eviscerato: false,
      presenza_vermi: false,
      note_conservazione: ''
    });
    setFrazionamento({
      abilitato: false,
      frazioni: [{ 
        quantita: 0, 
        conservazione: '', 
        giorni_scadenza: 3,
        inizio_data: getTodayInput(),
        inizio_ora: '',
        fine_data: getTodayInput(),
        fine_ora: '',
        temperatura: -18,
        eviscerato: false,
        presenza_vermi: false,
        note_conservazione: ''
      }]
    });
    await fetchNumeroLotto();
  };

  // NUOVO: Cerca prodotti nei DDT per riconciliazione
  const cercaProdottiDdt = async (query) => {
    if (!query || query.length < 2) return;
    
    setSearchingDdt(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/gestione/haccp/cerca-ingrediente-ddt`, {
        params: { nome: query, limit: 10 },
        headers: getApiHeaders()
      });
      setDdtSearchResults(res.data.risultati || []);
      setDdtSearchResultsGruppi(res.data.gruppi || []);
    } catch (error) {
      console.error('Errore ricerca DDT:', error);
      setDdtSearchResults([]);
      setDdtSearchResultsGruppi([]);
    } finally {
      setSearchingDdt(false);
    }
  };

  // NUOVO: Cerca storico DDT per un ingrediente (ultimi 5 acquisti)
  const cercaStoricoDdt = async (ingrediente, idx) => {
    const nomeRicerca = ingrediente.nome_prodotto_ddt || ingrediente.nome_ingrediente;
    if (!nomeRicerca) return;
    
    setStoricoDdtIngrediente(ingrediente);
    setStoricoDdtIngredienteIdx(idx);
    setShowStoricoDdtModal(true);
    setLoadingStoricoDdt(true);
    
    try {
      // Stessa ricerca di "Aggiungi" (DDT + fatture), raggruppata per prodotto e dall'ultimo arrivo
      const res = await axios.get(`${API}/gestione/haccp/cerca-ingrediente-ddt`, {
        params: { nome: nomeRicerca, limit: 10 },
        headers: getAuthHeaders()
      });
      setStoricoDdtResults(res.data.risultati || []);
    } catch (error) {
      console.error('Errore ricerca storico DDT:', error);
      
      setStoricoDdtResults([]);
    } finally {
      setLoadingStoricoDdt(false);
    }
  };

  // NUOVO: Seleziona DDT alternativo per un ingrediente nel lotto
  const selezionaDdtAlternativo = async (ddtItem) => {
    if (!selectedLotto || storicoDdtIngredienteIdx === null) return;
    
    try {
      // Aggiorna l'ingrediente nel lotto con i nuovi dati DDT
      const nuoviIngredienti = [...(selectedLotto.ingredienti_riconciliati || [])];
      nuoviIngredienti[storicoDdtIngredienteIdx] = {
        ...nuoviIngredienti[storicoDdtIngredienteIdx],
        nome_prodotto_ddt: ddtItem.nome_prodotto,
        numero_lotto: ddtItem.numero_lotto || '',
        fornitore: ddtItem.fornitore || '',
        ddt_numero: ddtItem.ddt_numero,
        data_ddt: ddtItem.data_ddt,
        ddt_id: ddtItem.ddt_id,
        scadenza_prodotto: ddtItem.scadenza || ''
      };
      
      // Salva sul backend (allergeni ricalcolati in base agli ingredienti)
      setSelectedLotto(await salvaModificaEtichetta(selectedLotto.id, { ingredienti_riconciliati: nuoviIngredienti }));
      
      toast.success('DDT aggiornato per l\'ingrediente. Ristampa l\'etichetta per aggiornarla.');
      setShowStoricoDdtModal(false);
      setStoricoDdtIngrediente(null);
      setStoricoDdtResults([]);
      
      // Ricarica lotti
      loadLotti();
    } catch (error) {
      console.error('Errore aggiornamento DDT:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  // NUOVO: Cerca ricetta con AI
  const cercaRicettaConAI = async () => {
    if (!nuovaRicetta.nome || nuovaRicetta.nome.trim().length < 3) {
      toast.error('Inserisci il nome del piatto (almeno 3 caratteri)');
      return;
    }
    
    setCercandoRicettaAI(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/gestione/haccp/ricetta-ai`, {
        nome_piatto: nuovaRicetta.nome.trim()
      }, { headers });
      
      if (res.data.success && res.data.ingredienti && res.data.ingredienti.length > 0) {
        // Aggiungi gli ingredienti trovati
        const nuoviIngredienti = res.data.ingredienti.map((ing, idx) => ({
          id: Date.now() + idx,
          nome: ing.nome,
          quantita: ing.quantita || 0,
          unita_misura: ing.unita_misura || 'g',
          lotto: '',
          scadenza: '',
          riconciliato: false,
          prodotto_ddt: null
        }));
        
        setNuovaRicetta(prev => ({
          ...prev,
          ingredienti: [...prev.ingredienti, ...nuoviIngredienti]
        }));
        
        toast.success(`Trovati ${nuoviIngredienti.length} ingredienti per "${nuovaRicetta.nome}". Riconciliazione in corso...`);
        
        // Auto-riconcilia gli ingredienti con i DDT
        await autoRiconciliaIngredientiCreaRicetta(nuoviIngredienti);
        
      } else {
        toast.error(`Ricetta "${nuovaRicetta.nome}" non trovata. Prova con un nome più comune.`);
      }
    } catch (error) {
      console.error('Errore ricerca ricetta AI:', error);
      toast.error('Errore nella ricerca AI. Riprova.');
    } finally {
      setCercandoRicettaAI(false);
    }
  };

  // Auto-riconcilia ingredienti per Crea Ricetta
  const autoRiconciliaIngredientiCreaRicetta = async (ingredienti) => {
    const token = localStorage.getItem('token');
    const tenantDb = localStorage.getItem('tenant_db') ;
    
    let riconciliatiCount = 0;
    const ingredientiAggiornati = [];
    
    for (const ing of ingredienti) {
      try {
        const res = await axios.get(`${API}/gestione/haccp/cerca-ingrediente-ddt`, {
          params: { nome: ing.nome, limit: 5 },
          headers: { 
            'Authorization': `Bearer ${token}`,
            'X-Tenant-DB': tenantDb
          }
        });
        
        const matches = res.data.risultati || [];
        const bestMatch = matches.find(m => m.numero_lotto) || matches[0];
        
        if (bestMatch) {
          ingredientiAggiornati.push({
            ...ing,
            riconciliato: true,
            lotto: bestMatch.numero_lotto ? String(bestMatch.numero_lotto) : '',
            scadenza: bestMatch.scadenza || '',
            prodotto_ddt: {
              nome_prodotto: bestMatch.nome_prodotto,
              fornitore: bestMatch.fornitore,
              numero_lotto: bestMatch.numero_lotto,
              scadenza: bestMatch.scadenza,
              ddt_id: bestMatch.ddt_id,
              ddt_numero: bestMatch.ddt_numero,
              data_ddt: bestMatch.data_ddt
            }
          });
          riconciliatiCount++;
        } else {
          ingredientiAggiornati.push(ing);
        }
      } catch (error) {
        console.error(`Errore riconciliazione ${ing.nome}:`, error);
        ingredientiAggiornati.push(ing);
      }
    }
    
    // Aggiorna lo stato con gli ingredienti riconciliati
    setNuovaRicetta(prev => ({
      ...prev,
      ingredienti: prev.ingredienti.map(existing => {
        const aggiornato = ingredientiAggiornati.find(a => a.id === existing.id);
        return aggiornato || existing;
      })
    }));
    
    if (riconciliatiCount > 0) {
      toast.success(`✓ Riconciliati ${riconciliatiCount}/${ingredienti.length} ingredienti con DDT`);
    } else {
      toast.info('Nessun ingrediente trovato nei DDT. Puoi riconciliarli manualmente.');
    }
  };

  // NUOVO: Aggiungi ingrediente alla ricetta manuale
  const aggiungiIngredienteCrea = () => {
    if (!nuovoIngredienteCrea.nome || !nuovoIngredienteCrea.quantita) {
      toast.error('Inserisci nome e quantità');
      return;
    }
    
    setNuovaRicetta(prev => ({
      ...prev,
      ingredienti: [...prev.ingredienti, {
        ...nuovoIngredienteCrea,
        id: Date.now(),
        riconciliato: !!nuovoIngredienteCrea.prodotto_ddt
      }]
    }));
    
    // Reset form
    setNuovoIngredienteCrea({
      nome: '',
      quantita: '',
      unita_misura: 'g',
      lotto: '',
      scadenza: '',
      prodotto_ddt: null
    });
    setDdtSearchResults([]);
  };

  // NUOVO: Rimuovi ingrediente dalla ricetta manuale
  const rimuoviIngredienteCrea = (idx) => {
    setNuovaRicetta(prev => ({
      ...prev,
      ingredienti: prev.ingredienti.filter((_, i) => i !== idx)
    }));
  };

  // Apri modifica ingrediente
  const apriModificaIngrediente = async (idx) => {
    setEditingIngredienteIdx(idx);
    const ing = nuovaRicetta.ingredienti[idx];
    // Cerca prodotti DDT per questo ingrediente
    await cercaDdtPerModifica(ing.nome);
  };

  // Cerca DDT per modifica ingrediente
  const cercaDdtPerModifica = async (nome) => {
    setSearchingDdtForEdit(true);
    try {
      const token = localStorage.getItem('token');
      const tenantDb = localStorage.getItem('tenant_db') ;
      const res = await axios.get(`${API}/gestione/haccp/cerca-ingrediente-ddt`, {
        params: { nome, limit: 10 },
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Tenant-DB': tenantDb
        }
      });
      setDdtSearchForEdit(res.data.risultati || []);
      setDdtSearchForEditGruppi(res.data.gruppi || []);
    } catch (error) {
      console.error('Errore ricerca DDT:', error);
      setDdtSearchForEdit([]);
      setDdtSearchForEditGruppi([]);
    } finally {
      setSearchingDdtForEdit(false);
    }
  };

  // Seleziona nuovo DDT per ingrediente
  const selezionaNuovoDdt = (idx, prodotto) => {
    setNuovaRicetta(prev => ({
      ...prev,
      ingredienti: prev.ingredienti.map((ing, i) => {
        if (i === idx) {
          return {
            ...ing,
            riconciliato: true,
            lotto: prodotto.numero_lotto ? String(prodotto.numero_lotto) : '',
            scadenza: prodotto.scadenza || '',
            prodotto_ddt: {
              nome_prodotto: prodotto.nome_prodotto,
              fornitore: prodotto.fornitore,
              numero_lotto: prodotto.numero_lotto,
              scadenza: prodotto.scadenza,
              ddt_id: prodotto.ddt_id,
              ddt_numero: prodotto.ddt_numero,
              data_ddt: prodotto.data_ddt
            }
          };
        }
        return ing;
      })
    }));
    setEditingIngredienteIdx(null);
    setDdtSearchForEdit([]);
    toast.success(`Ingrediente abbinato a "${prodotto.nome_prodotto}"`);
  };

  // Rimuovi abbinamento DDT
  const rimuoviAbbinamentoDdt = (idx) => {
    setNuovaRicetta(prev => ({
      ...prev,
      ingredienti: prev.ingredienti.map((ing, i) => {
        if (i === idx) {
          return {
            ...ing,
            riconciliato: false,
            lotto: '',
            scadenza: '',
            prodotto_ddt: null
          };
        }
        return ing;
      })
    }));
    setEditingIngredienteIdx(null);
    toast.info('Abbinamento rimosso');
  };

  // NUOVO: Seleziona prodotto DDT per ingrediente (stessa logica di confermaRiconciliazione)
  const selezionaProdottoDdt = (prodotto) => {
    // Usa numero_lotto come in confermaRiconciliazione
    const lottoValue = prodotto.numero_lotto || prodotto.lotto || '';
    
    setNuovoIngredienteCrea(prev => ({
      ...prev,
      nome: prodotto.nome_prodotto || prodotto.descrizione || prodotto.nome,
      lotto: lottoValue ? String(lottoValue) : '',
      scadenza: prodotto.scadenza || '',
      prodotto_ddt: {
        ...prodotto,
        nome_prodotto_ddt: prodotto.nome_prodotto || prodotto.nome,
        numero_lotto: lottoValue ? String(lottoValue) : null
      }
    }));
    setDdtSearchResults([]);
    
    const nomeDisplay = prodotto.nome_prodotto || prodotto.descrizione || prodotto.nome;
    if (lottoValue) {
      toast.success(`Selezionato: ${nomeDisplay} - Lotto: ${lottoValue}`);
    } else {
      toast.success(`Selezionato: ${nomeDisplay}`);
    }
  };

  // Toggle manuale allergene (aggiungi/rimuovi)
  const toggleAllergene = (a) => {
    setAllergeniTouched(true);
    setAllergeniSelezionati((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  };

  // Analizza allergeni in tempo reale durante la preparazione del lotto
  useEffect(() => {
    if (!showCreaRicettaModal && !showImportModal) {
      setAllergeniAuto([]);
      setAllergeniSelezionati([]);
      setAllergeniTouched(false);
      return;
    }
    const nomi = [];
    if (showCreaRicettaModal) {
      if (nuovaRicetta.nome) nomi.push(nuovaRicetta.nome);
      nuovaRicetta.ingredienti.forEach((i) => {
        if (i.nome) nomi.push(i.nome);
        const ddt = i.prodotto_ddt?.descrizione || i.prodotto_ddt?.nome;
        if (ddt) nomi.push(ddt);
      });
    } else {
      if (selectedRicetta?.nome) nomi.push(selectedRicetta.nome);
      ingredientiRiconciliati.forEach((i) => {
        if (i.nome_ingrediente) nomi.push(i.nome_ingrediente);
        if (i.nome_prodotto_ddt) nomi.push(i.nome_prodotto_ddt);
      });
    }
    if (nomi.length === 0) {
      setAllergeniAuto([]);
      if (!allergeniTouched) setAllergeniSelezionati([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/gestione/haccp/analizza-allergeni-preview`, { ingredienti: nomi }, { headers: getApiHeaders() });
        const auto = res.data.allergeni || [];
        setAllergeniAuto(auto);
        setAllergeniSelezionati((prev) => (allergeniTouched ? prev : auto));
      } catch (e) {
        console.error('Errore analisi allergeni:', e);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [showCreaRicettaModal, showImportModal, nuovaRicetta, ingredientiRiconciliati, selectedRicetta, allergeniTouched]);

  // NUOVO: Salva lotto da ricetta creata manualmente
  const salvaLottoCreaRicetta = async () => {
    if (!nuovaRicetta.nome) {
      toast.error('Inserisci il nome del piatto');
      return;
    }
    if (nuovaRicetta.ingredienti.length === 0) {
      toast.error('Aggiungi almeno un ingrediente');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Formatta data produzione in DD/MM/YYYY
      const dataProd = new Date(lottoForm.data_produzione);
      const dataProdStr = `${String(dataProd.getDate()).padStart(2, '0')}/${String(dataProd.getMonth() + 1).padStart(2, '0')}/${dataProd.getFullYear()}`;
      
      // Prepara ingredienti nel formato IngredienteLottoCreate
      const ingredientiRiconciliati = nuovaRicetta.ingredienti.map(ing => ({
        nome_ingrediente: ing.nome,
        nome_prodotto_ddt: ing.prodotto_ddt?.descrizione || ing.prodotto_ddt?.nome || null,
        fornitore: ing.prodotto_ddt?.fornitore || null,
        numero_lotto: ing.lotto ? String(ing.lotto) : null,
        scadenza_prodotto: ing.scadenza || null,
        ddt_id: ing.prodotto_ddt?.ddt_id || null,
        ddt_numero: ing.prodotto_ddt?.ddt_numero ? String(ing.prodotto_ddt.ddt_numero) : null,
        data_ddt: ing.prodotto_ddt?.data_ddt || null,
        quantita: parseFloat(ing.quantita) || null,
        unita_misura: ing.unita_misura || null
      }));

      // Formatta date abbattimento se necessario
      const formatAbbDate = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      };

      // Scadenza lotto: data precisa, giorni espliciti, oppure default backend (metodo/3gg)
      let dataScadenzaCreaStr = null;
      let giorniScadenzaCrea = null;
      if (lottoForm.tipo_scadenza === 'custom' && lottoForm.scadenza_custom) {
        const scadCustom = new Date(lottoForm.scadenza_custom);
        dataScadenzaCreaStr = `${String(scadCustom.getDate()).padStart(2, '0')}/${String(scadCustom.getMonth() + 1).padStart(2, '0')}/${scadCustom.getFullYear()}`;
      } else if (lottoForm.tipo_scadenza) {
        giorniScadenzaCrea = parseInt(lottoForm.tipo_scadenza);
        const dataScadCrea = new Date(dataProd);
        dataScadCrea.setDate(dataScadCrea.getDate() + giorniScadenzaCrea);
        dataScadenzaCreaStr = `${String(dataScadCrea.getDate()).padStart(2, '0')}/${String(dataScadCrea.getMonth() + 1).padStart(2, '0')}/${dataScadCrea.getFullYear()}`;
      }

      const payload = {
        ricetta_id: `manual_${Date.now()}`, // ID temporaneo per ricette manuali
        ricetta_nome: nuovaRicetta.nome,
        data_produzione: dataProdStr,
        ...(dataScadenzaCreaStr ? { data_scadenza: dataScadenzaCreaStr, giorni_scadenza: giorniScadenzaCrea } : {}),
        quantita: parseFloat(lottoForm.quantita) || 1,
        unita_misura: lottoForm.unita_misura || 'porzioni',
        ingredienti_riconciliati: ingredientiRiconciliati,
        abbattimento: conservazione.necessaria,
        abbattimento_inizio_data: conservazione.necessaria ? formatAbbDate(conservazione.inizio_data) : null,
        abbattimento_inizio_ora: conservazione.necessaria ? conservazione.inizio_ora : null,
        abbattimento_fine_data: conservazione.necessaria ? formatAbbDate(conservazione.fine_data) : null,
        abbattimento_fine_ora: conservazione.necessaria ? conservazione.fine_ora : null,
        temperatura_abbattimento: conservazione.necessaria ? parseFloat(conservazione.temperatura) : null,
        eviscerato: conservazione.necessaria ? conservazione.eviscerato : null,
        presenza_vermi: conservazione.necessaria ? conservazione.presenza_vermi : null,
        metodo_conservazione: conservazione.necessaria ? conservazione.metodo : null,
        note_conservazione: conservazione.necessaria ? conservazione.note_conservazione : null,
        note: lottoForm.note || null,
        ...(allergeniTouched ? { allergeni_manuali: allergeniSelezionati } : {})
      };

      console.log('Payload Crea Ricetta:', payload);

      const res = await axios.post(`${API}/gestione/haccp/lotti-produzione-avanzati`, payload, {
        headers: getApiHeaders()
      });
      
      toast.success('Lotto creato con successo!');
      
      // Chiudi modal e ricarica
      setShowCreaRicettaModal(false);
      setCreaRicettaStep(1);
      setNuovaRicetta({ nome: '', ingredienti: [] });
      loadLotti();
      
      // Scarica etichetta se disponibile
      if (res.data.lotto?.id) {
        downloadPdfEtichetta(res.data.lotto.id);
      }
      
    } catch (error) {
      console.error('Errore salvataggio lotto:', error);
      let errorMsg = 'Errore nel salvataggio';
      if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Chiudi modal e resetta
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportStep(1);
    setSelectedRicetta(null);
    setNumeroLotto('');
    setIngredientiRiconciliati([]);
    setRicettaSearch('');
    setLottoForm({
      data_produzione: getTodayInput(),
      quantita: 1,
      unita_misura: 'porzioni',
      note: '',
      tipo_scadenza: '', // Nessuna preselezione
      scadenza_custom: ''
    });
    setConservazione({
      necessaria: false,
      metodo: '',
      inizio_data: getTodayInput(),
      inizio_ora: '',
      fine_data: getTodayInput(),
      fine_ora: '',
      temperatura: -18,
      eviscerato: false,
      presenza_vermi: false,
      note_conservazione: ''
    });
    setFrazionamento({
      abilitato: false,
      frazioni: [{ 
        quantita: 0, 
        conservazione: '', 
        giorni_scadenza: 3,
        inizio_data: getTodayInput(),
        inizio_ora: '',
        fine_data: getTodayInput(),
        fine_ora: '',
        temperatura: -18,
        eviscerato: false,
        presenza_vermi: false,
        note_conservazione: ''
      }]
    });
  };

  const filteredLotti = lotti.filter(l => 
    l.ricetta_nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.numero_lotto?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Ordina per numero lotto decrescente (più recente in alto)
  const sortedLotti = [...filteredLotti].sort((a, b) => {
    // Estrai il numero dal formato "YYYY-NNNN" o "NNNN"
    const getNumero = (lotto) => {
      if (!lotto.numero_lotto) return 0;
      const parts = lotto.numero_lotto.split('-');
      if (parts.length >= 2) {
        // Formato "2026-0001" -> ordina per anno poi per numero
        const anno = parseInt(parts[0]) || 0;
        const num = parseInt(parts[1]) || 0;
        return anno * 10000 + num;
      }
      return parseInt(lotto.numero_lotto) || 0;
    };
    return getNumero(b) - getNumero(a);
  });

  // Dividi lotti in SCADUTI e ATTIVI
  const isLottoScaduto = (lotto) => {
    if (!lotto.data_scadenza) return false;
    const parts = lotto.data_scadenza.split('/');
    if (parts.length !== 3) return false;
    const scadenza = new Date(parts[2], parts[1] - 1, parts[0]);
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    return scadenza < oggi;
  };

  const lottiScaduti = sortedLotti.filter(l => isLottoScaduto(l));
  const lottiAttivi = sortedLotti.filter(l => !isLottoScaduto(l));

  // Selezione multipla (limitata ai lotti visibili nella scheda)
  const selAttiveVisibili = lottiAttivi.filter(l => selAttive.includes(l.id)).map(l => l.id);
  const selScaduteVisibili = lottiScaduti.filter(l => selScadute.includes(l.id)).map(l => l.id);
  const toggleSel = (setter, id) => setter(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const toggleTutte = (setter, lista, tutteSelezionate) => setter(tutteSelezionate ? [] : lista.map(l => l.id));

  const stampaSelezionate = async () => {
    const tenantDb = localStorage.getItem('tenant_db');
    if (!tenantDb) {
      toast.error('Errore: tenant non configurato');
      return;
    }
    const n = selAttiveVisibili.length;
    // Apertura diretta e sincrona dell'URL: unico metodo affidabile nella PWA su iPhone
    const url = `${API}/haccp/genera-etichette-pdf-multiple?tenant=${encodeURIComponent(tenantDb)}&ids=${selAttiveVisibili.map(encodeURIComponent).join(',')}`;
    const printWindow = window.open(url, '_blank');
    if (!printWindow) {
      window.location.href = url;
    }
    toast.success(`${n} etichett${n === 1 ? 'a pronta' : 'e pronte'}: usa Condividi > Stampa del PDF`);
    setSelAttive([]);
  };

  const eliminaSelezionate = async () => {
    const n = selScaduteVisibili.length;
    if (!window.confirm(`Eliminare definitivamente ${n} etichett${n === 1 ? 'a scaduta' : 'e scadute'}?`)) {
      return;
    }
    setAzioneMultipla(true);
    try {
      const res = await axios.post(
        `${API}/gestione/haccp/lotti-produzione-avanzati/elimina-multipli`,
        { ids: selScaduteVisibili },
        { headers: getApiHeaders() }
      );
      const eliminati = res.data.eliminati ?? 0;
      toast.success(`${eliminati} etichett${eliminati === 1 ? 'a eliminata' : 'e eliminate'}`);
      setSelScadute([]);
      loadLotti();
    } catch (error) {
      console.error('Errore eliminazione multipla:', error);
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setAzioneMultipla(false);
    }
  };

  // Helper: calcola giorni alla scadenza e stato alert
  const getScadenzaAlert = (dataScadenza) => {
    if (!dataScadenza) return { giorni: null, alert: 'none' };
    
    // Parse data nel formato DD/MM/YYYY
    const parts = dataScadenza.split('/');
    if (parts.length !== 3) return { giorni: null, alert: 'none' };
    
    const scadenza = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    scadenza.setHours(0, 0, 0, 0);
    
    const diffTime = scadenza.getTime() - oggi.getTime();
    const diffGiorni = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffGiorni < 0) {
      return { giorni: diffGiorni, alert: 'scaduto' };
    } else if (diffGiorni <= 3) {
      return { giorni: diffGiorni, alert: 'warning' };
    }
    return { giorni: diffGiorni, alert: 'ok' };
  };

  // Normalizza stringa per ricerca (rimuove accenti e caratteri speciali)
  const normalizeString = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Rimuove accenti
      .replace(/[^a-z0-9\s]/g, ''); // Rimuove caratteri speciali
  };

  // Filtra ricette - Se la ricerca è vuota, mostra tutte
  const filteredRicette = ricettaSearch.trim() === '' 
    ? ricette 
    : ricette.filter(r => {
        const searchNorm = normalizeString(ricettaSearch);
        const nomeNorm = normalizeString(r.nome);
        const found = nomeNorm.includes(searchNorm);
        return found;
      });
  
  // Debug: log solo quando cambia la ricerca
  useEffect(() => {
    if (ricettaSearch) {
      console.log(`🔍 Ricerca: "${ricettaSearch}" - Trovate: ${filteredRicette.length}/${ricette.length}`);
    }
  }, [ricettaSearch, filteredRicette.length, ricette.length]);

  const ingredientiRiconciliatiCount = ingredientiRiconciliati.filter(i => i.riconciliato).length;

  // Mostra QR Code
  const showQr = (lotto) => {
    setSelectedLotto(lotto);
    setQrCodeUrl(`${API}/gestione/haccp/qr-code/${lotto.id}`);
    setShowQrModal(true);
  };

  // Mostra Dettaglio Lotto
  const showDettaglio = async (lotto) => {
    setSelectedLotto(lotto);
    setEditMode(false);
    setEditingLotto(null);
    setShowDettaglioModal(true);
    
    // Se non ha allergeni (e non sono stati tolti a mano), analizzali automaticamente senza avvisi
    const hasAllergeni = (lotto.allergeni_totali?.length > 0) || (Array.isArray(lotto.allergeni) && lotto.allergeni.length > 0);
    if (!hasAllergeni && !lotto.allergeni_modificati_manualmente) {
      await analizzaAllergeni(lotto.id, true);
    }
  };

  // Attiva modalità modifica
  const startEditLotto = () => {
    if (!selectedLotto) return;
    
    // Converti date dal formato DD/MM/YYYY a YYYY-MM-DD per input date
    const parseDate = (dateStr) => {
      if (!dateStr) return '';
      if (dateStr.includes('-')) return dateStr;
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
      return '';
    };
    
    setEditingLotto({
      data_produzione: parseDate(selectedLotto.data_produzione),
      data_scadenza: parseDate(selectedLotto.data_scadenza),
      quantita: selectedLotto.quantita || 1,
      unita_misura: selectedLotto.unita_misura || 'porzioni',
      abbattimento: selectedLotto.abbattimento || false,
      abbattimento_inizio_data: parseDate(selectedLotto.abbattimento_inizio_data),
      abbattimento_inizio_ora: selectedLotto.abbattimento_inizio_ora || '',
      abbattimento_fine_data: parseDate(selectedLotto.abbattimento_fine_data),
      abbattimento_fine_ora: selectedLotto.abbattimento_fine_ora || '',
      temperatura_abbattimento: selectedLotto.temperatura_abbattimento || -18,
      eviscerato: selectedLotto.eviscerato || false,
      presenza_vermi: selectedLotto.presenza_vermi || false,
      note: selectedLotto.note || ''
    });
    setEditMode(true);
  };

  // Annulla modifica
  const cancelEditLotto = () => {
    setEditMode(false);
    setEditingLotto(null);
  };

  // Salva modifiche lotto
  const saveEditLotto = async () => {
    if (!selectedLotto || !editingLotto) return;
    if (!editingLotto.data_produzione || !editingLotto.data_scadenza) {
      toast.error('Inserisci la data di produzione e la data di scadenza');
      return;
    }
    if (editingLotto.data_scadenza < editingLotto.data_produzione) {
      toast.error('La scadenza non può essere prima della produzione');
      return;
    }
    
    setSavingEdit(true);
    try {
      // Converti date da YYYY-MM-DD a DD/MM/YYYY
      const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
      };
      
      const payload = {
        data_produzione: formatDate(editingLotto.data_produzione),
        data_scadenza: formatDate(editingLotto.data_scadenza),
        quantita: parseFloat(editingLotto.quantita) || 1,
        unita_misura: editingLotto.unita_misura,
        abbattimento: editingLotto.abbattimento,
        abbattimento_inizio_data: editingLotto.abbattimento ? formatDate(editingLotto.abbattimento_inizio_data) : null,
        abbattimento_inizio_ora: editingLotto.abbattimento ? editingLotto.abbattimento_inizio_ora : null,
        abbattimento_fine_data: editingLotto.abbattimento ? formatDate(editingLotto.abbattimento_fine_data) : null,
        abbattimento_fine_ora: editingLotto.abbattimento ? editingLotto.abbattimento_fine_ora : null,
        temperatura_abbattimento: editingLotto.abbattimento ? parseFloat(editingLotto.temperatura_abbattimento) : null,
        eviscerato: editingLotto.abbattimento ? editingLotto.eviscerato : null,
        presenza_vermi: editingLotto.abbattimento ? editingLotto.presenza_vermi : null,
        note: editingLotto.note
      };
      
      const lotto = await salvaModificaEtichetta(selectedLotto.id, payload);
      toast.success('Etichetta modificata. Ristampala per aggiornare quella attaccata.');
      setSelectedLotto(lotto);
      setEditMode(false);
      setEditingLotto(null);
      loadLotti();
    } catch (error) {
      console.error('Errore modifica lotto:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la modifica');
    } finally {
      setSavingEdit(false);
    }
  };

  // Analizza Allergeni con AI
  const analizzaAllergeni = async (lottoId, silenzioso = false) => {
    setAnalizzandoAllergeni(true);
    try {
      const token = localStorage.getItem('tenant_token') || localStorage.getItem('token');
      const userStr = localStorage.getItem('tenant_user') || localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const tenantDb = user.tenants && user.tenants[0] ? user.tenants[0].database_name : '';
      const tenantId = tenantDb.replace('tenant_', '') ;
      
      const res = await axios.post(`${API}/gestione/haccp/analizza-allergeni/${lottoId}`, {}, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Tenant-DB': tenantId
        }
      });
      
      if (res.data.success) {
        if (!silenzioso) toast.success('Allergeni analizzati con successo!');
        // Aggiorna il lotto selezionato con gli allergeni
        setSelectedLotto(prev => ({
          ...prev,
          allergeni_analisi: res.data.allergeni,
          allergeni_totali: res.data.allergeni_totali || res.data.allergeni?.allergeni_totali,
          allergeni: res.data.allergeni_list || []
        }));
        // Ricarica i lotti
        loadLotti();
      }
    } catch (error) {
      console.error('Errore analisi allergeni:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'analisi degli allergeni');
    } finally {
      setAnalizzandoAllergeni(false);
    }
  };

  // Download Scheda PDF Dettagliata
  const downloadSchedaPdf = async (lottoId) => {
    try {
      const tenantDb = localStorage.getItem('tenant_db');
      
      if (!tenantDb) {
        toast.error('Errore: tenant non configurato');
        return;
      }
      
      // Apri direttamente l'URL della scheda in una nuova tab
      const url = `${API}/gestione/haccp/genera-scheda-pdf/${lottoId}?tenant=${tenantDb}`;
      window.open(url, '_blank');
      toast.success('Scheda aperta! Usa il pulsante Stampa nella pagina.');
      
    } catch (error) {
      console.error('Errore generazione scheda PDF:', error);
      toast.error('Errore nella generazione della scheda');
    }
  };

  // Elimina Lotto
  const eliminaLotto = async (lottoId, numeroLotto) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il lotto ${numeroLotto}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/gestione/haccp/lotti-produzione-avanzati/${lottoId}`, {
        headers: getApiHeaders()
      });
      
      if (res.data.success) {
        toast.success(`Lotto ${numeroLotto} eliminato`);
        setShowDettaglioModal(false);
        setSelectedLotto(null);
        loadLotti();
      }
    } catch (error) {
      console.error('Errore eliminazione lotto:', error);
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // Apri DDT originale
  const apriDDT = (ddtId) => {
    // Apri la pagina DDT in una nuova tab o mostra il DDT
    if (ddtId) {
      window.open(`/ddt/${ddtId}`, '_blank');
    } else {
      toast.error('DDT non disponibile');
    }
  };

  // Apri modal per aggiungere nuovo ingrediente
  const openAddIngredienteModal = (isDettaglio = false) => {
    setAddingToDettaglio(isDettaglio);
    setNuovoIngredienteNome('');
    setNuovoIngredienteDdtResults([]);
    setShowAddIngredienteModal(true);
  };

  // Cerca prodotto nei DDT per nuovo ingrediente
  const cercaNuovoIngrediente = async () => {
    if (!nuovoIngredienteNome.trim()) return;
    
    setSearchingNewIngrediente(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/gestione/haccp/cerca-ingrediente-ddt`, {
        params: { nome: nuovoIngredienteNome, limit: 15 },
        headers: getApiHeaders()
      });
      setNuovoIngredienteDdtResults(res.data.risultati || []);
      setNuovoIngredienteDdtGruppi(res.data.gruppi || []);
    } catch (error) {
      console.error('Errore ricerca:', error);
      setNuovoIngredienteDdtResults([]);
      setNuovoIngredienteDdtGruppi([]);
    } finally {
      setSearchingNewIngrediente(false);
    }
  };

  // Aggiungi ingrediente manualmente (senza DDT)
  const aggiungiIngredienteManuale = () => {
    if (!nuovoIngredienteNome.trim()) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }
    
    const nuovoIng = {
      id: Date.now(),
      nome_ingrediente: nuovoIngredienteNome,
      riconciliato: false,
      nome_prodotto_ddt: null,
      fornitore: null,
      numero_lotto: null,
      scadenza_prodotto: null,
      ddt_id: null,
      ddt_numero: null,
      data_ddt: null
    };
    
    if (addingToDettaglio && selectedLotto) {
      // Aggiungi a lotto esistente
      aggiungiIngredienteALotto(nuovoIng);
    } else {
      // Aggiungi a lista durante creazione
      setIngredientiRiconciliati(prev => [...prev, nuovoIng]);
      toast.success('Prodotto aggiunto');
    }
    
    setShowAddIngredienteModal(false);
  };

  // Aggiungi ingrediente con dati DDT
  const aggiungiIngredienteConDdt = (ddtItem) => {
    const nuovoIng = {
      id: Date.now(),
      nome_ingrediente: ddtItem.nome_prodotto,
      riconciliato: true,
      nome_prodotto_ddt: ddtItem.nome_prodotto,
      fornitore: ddtItem.fornitore,
      numero_lotto: ddtItem.numero_lotto ? String(ddtItem.numero_lotto) : null,
      scadenza_prodotto: ddtItem.scadenza,
      ddt_id: ddtItem.ddt_id,
      ddt_numero: ddtItem.ddt_numero != null ? String(ddtItem.ddt_numero) : null,
      data_ddt: ddtItem.data_ddt
    };
    
    if (addingToDettaglio && selectedLotto) {
      // Aggiungi a lotto esistente
      aggiungiIngredienteALotto(nuovoIng);
    } else {
      // Aggiungi a lista durante creazione
      setIngredientiRiconciliati(prev => [...prev, nuovoIng]);
      toast.success('Prodotto aggiunto');
    }
    
    setShowAddIngredienteModal(false);
  };

  // Aggiungi ingrediente a lotto esistente (salva su DB)
  const aggiungiIngredienteALotto = async (nuovoIng) => {
    try {
      const nuoviIngredienti = [...(selectedLotto.ingredienti_riconciliati || []), nuovoIng];
      setSelectedLotto(await salvaModificaEtichetta(selectedLotto.id, { ingredienti_riconciliati: nuoviIngredienti }));
      toast.success('Prodotto aggiunto. Ristampa l\'etichetta per aggiornarla.');
      loadLotti();
    } catch (error) {
      console.error('Errore aggiunta ingrediente:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiunta');
    }
  };

  // Inizia editing nome ingrediente
  const startEditIngrediente = (ing) => {
    setEditingIngredienteId(ing.id);
    setEditingIngredienteNome(ing.nome_ingrediente);
  };

  // Salva modifica nome ingrediente
  const saveEditIngrediente = (ingredienteId) => {
    if (!editingIngredienteNome.trim()) {
      toast.error('Il nome non può essere vuoto');
      return;
    }
    
    setIngredientiRiconciliati(prev => prev.map(ing => 
      ing.id === ingredienteId 
        ? { ...ing, nome_ingrediente: editingIngredienteNome.trim() }
        : ing
    ));
    
    setEditingIngredienteId(null);
    setEditingIngredienteNome('');
    toast.success('Nome modificato');
  };

  // Annulla editing
  const cancelEditIngrediente = () => {
    setEditingIngredienteId(null);
    setEditingIngredienteNome('');
  };

  // Rimuovi ingrediente dalla lista (durante creazione)
  const rimuoviIngrediente = (ingredienteId) => {
    setIngredientiRiconciliati(prev => prev.filter(ing => ing.id !== ingredienteId));
    toast.success('Prodotto rimosso');
  };

  // Rimuovi ingrediente da lotto esistente (usando indice array)
  const rimuoviIngredienteDaLotto = async (ingredienteIndex) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo prodotto?')) return;
    
    try {
      const nuoviIngredienti = (selectedLotto.ingredienti_riconciliati || [])
        .filter((_, idx) => idx !== ingredienteIndex);
      setSelectedLotto(await salvaModificaEtichetta(selectedLotto.id, { ingredienti_riconciliati: nuoviIngredienti }));
      toast.success('Prodotto rimosso. Ristampa l\'etichetta per aggiornarla.');
      loadLotti();
    } catch (error) {
      console.error('Errore rimozione ingrediente:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la rimozione');
    }
  };

  return (
    <div className="p-4">
      {/* Search and Add Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
          <input
            type="text"
            placeholder="Cerca lotti..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-black/40 backdrop-blur-md lg:bg-white border-slate-700/50 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-white lg:text-gray-900 placeholder-slate-400 lg:placeholder-gray-400"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-slate-300 lg:text-gray-700 rounded-xl font-medium text-sm transition-colors border border-slate-700 lg:border-transparent"
            title="Impostazioni Etichetta"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Impostazioni</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nuovo Lotto
          </button>
        </div>
      </div>

      {/* Etichette vocali con Siri */}
      <SiriEtichetteCard />

      {/* Tab Selector - Attive / Scadute */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('attive')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-all ${
            activeTab === 'attive'
              ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
              : 'bg-black/40 lg:bg-gray-100 text-slate-400 lg:text-gray-500 hover:bg-slate-700 lg:hover:bg-gray-200 border border-slate-700 lg:border-transparent'
          }`}
        >
          <Check className="h-4 w-4" />
          Attive ({lottiAttivi.length})
        </button>
        <button
          onClick={() => setActiveTab('scadute')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-all ${
            activeTab === 'scadute'
              ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
              : 'bg-black/40 lg:bg-gray-100 text-slate-400 lg:text-gray-500 hover:bg-slate-700 lg:hover:bg-gray-200 border border-slate-700 lg:border-transparent'
          }`}
        >
          <AlertTriangle className="h-4 w-4" />
          Scadute ({lottiScaduti.length})
        </button>
      </div>

      {/* Lotti List - Mobile Card Style */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredLotti.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-slate-600 lg:text-gray-300 mx-auto mb-3" />
          <p className="text-slate-400 lg:text-gray-500">Nessun lotto di produzione trovato</p>
          <p className="text-sm text-slate-500 lg:text-gray-400 mt-1">Crea il tuo primo lotto importando una ricetta</p>
        </div>
      ) : (
        <div className="space-y-4 lg:space-y-4">
          {/* SEZIONE ETICHETTE ATTIVE */}
          {activeTab === 'attive' && lottiAttivi.length > 0 && (
            <div>
              <SelezioneBar
                tone="green"
                total={lottiAttivi.length}
                selected={selAttiveVisibili.length}
                onToggleAll={() => toggleTutte(setSelAttive, lottiAttivi, selAttiveVisibili.length === lottiAttivi.length)}
                onAction={stampaSelezionate}
                actionLabel="Stampa selezionate"
                ActionIcon={Printer}
                busy={azioneMultipla}
                testidPrefix="etichette-attive"
              />
              {/* Mobile Cards - Attive */}
              <div className="lg:hidden space-y-2">
                {lottiAttivi.map((lotto) => {
                  const scadenzaInfo = getScadenzaAlert(lotto.data_scadenza);
                  const selezionato = selAttive.includes(lotto.id);
                  return (
                  <div 
                    key={lotto.id} 
                    data-testid={`lotto-attivo-card-${lotto.id}`}
                    className={`bg-black/40 backdrop-blur-md rounded-xl p-3 border active:scale-[0.99] transition-transform ${selezionato ? 'border-green-400 ring-2 ring-green-500/60' : 'border-green-500/30'}`}
                    onClick={() => showDettaglio(lotto)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <SelezioneCheckbox
                          tone="green"
                          checked={selezionato}
                          onToggle={() => toggleSel(setSelAttive, lotto.id)}
                          testid={`lotto-attivo-checkbox-${lotto.id}`}
                        />
                        <span className={`flex items-center justify-center px-2 py-1 rounded-lg text-xs font-bold border ${
                          scadenzaInfo.alert === 'warning'
                          ? 'bg-yellow-600/30 text-yellow-300 border-yellow-500/50'
                          : 'bg-green-600/20 text-green-400 border-green-500/30'
                        }`}>
                          {lotto.numero_lotto}
                          {scadenzaInfo.alert === 'warning' && <span className="ml-1">⏰</span>}
                        </span>
                      </div>
                    </div>
                    <p className="font-medium text-white text-sm mb-2">{lotto.ricetta_nome}<BadgeRichiamo lotto={lotto} /></p>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex gap-3 text-slate-400">
                        <span>Prod: {lotto.data_produzione}</span>
                        <span className={scadenzaInfo.alert === 'warning' ? 'text-yellow-400 font-bold' : ''}>
                          Scad: {lotto.data_scadenza}
                          {scadenzaInfo.alert === 'warning' && ` (tra ${scadenzaInfo.giorni}gg)`}
                        </span>
                      </div>
                      <span className="text-blue-400 font-medium">{lotto.quantita} {lotto.unita_misura}</span>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-700/50" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => showQr(lotto)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs font-medium">
                        <QrCode className="h-3.5 w-3.5" /> QR
                      </button>
                      <button onClick={() => downloadSchedaPdf(lotto.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs font-medium">
                        <Download className="h-3.5 w-3.5" /> PDF
                      </button>
                      <button onClick={() => downloadPdfEtichetta(lotto.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-xs font-medium">
                        <Printer className="h-3.5 w-3.5" /> Stampa
                      </button>
                    </div>
                  </div>
                )})}
              </div>
              
              {/* Desktop Table - Attive */}
              <div className="hidden lg:block overflow-x-auto bg-white rounded-xl border border-green-200">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100 bg-green-50">
                      <th className="pl-4 py-3 w-8"></th>
                      <th className="px-4 py-3 font-medium">Lotto</th>
                      <th className="py-3 font-medium">Prodotto</th>
                      <th className="py-3 font-medium">Data prod.</th>
                      <th className="py-3 font-medium">Scadenza</th>
                      <th className="py-3 font-medium">Qtà</th>
                      <th className="py-3 font-medium w-32">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {lottiAttivi.map((lotto) => {
                      const scadenzaInfo = getScadenzaAlert(lotto.data_scadenza);
                      return (
                      <tr key={lotto.id} className={`cursor-pointer transition-colors ${selAttive.includes(lotto.id) ? 'bg-green-50' : 'hover:bg-gray-50/50'}`} onClick={() => showDettaglio(lotto)}>
                        <td className="pl-4 py-3">
                          <SelezioneCheckbox
                            tone="green"
                            checked={selAttive.includes(lotto.id)}
                            onToggle={() => toggleSel(setSelAttive, lotto.id)}
                            testid={`lotto-attivo-checkbox-desktop-${lotto.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${scadenzaInfo.alert === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {lotto.numero_lotto}
                          </span>
                        </td>
                        <td className="py-3 font-medium text-gray-900">{lotto.ricetta_nome}<BadgeRichiamo lotto={lotto} /></td>
                        <td className="py-3 text-sm text-gray-600">{lotto.data_produzione}</td>
                        <td className={`py-3 text-sm ${scadenzaInfo.alert === 'warning' ? 'text-yellow-600 font-bold' : 'text-gray-600'}`}>
                          {lotto.data_scadenza} {scadenzaInfo.alert === 'warning' && `(${scadenzaInfo.giorni}gg)`}
                        </td>
                        <td className="py-3 text-sm text-gray-600">{lotto.quantita} {lotto.unita_misura}</td>
                        <td className="py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <button onClick={() => showQr(lotto)} className="p-1.5 hover:bg-gray-100 rounded" title="QR Code"><QrCode className="h-4 w-4 text-gray-500" /></button>
                            <button onClick={() => downloadPdfEtichetta(lotto.id)} className="p-1.5 hover:bg-green-100 rounded" title="Stampa"><Printer className="h-4 w-4 text-green-600" /></button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* SEZIONE ETICHETTE SCADUTE */}
          {activeTab === 'scadute' && lottiScaduti.length > 0 && (
            <div>
              <SelezioneBar
                tone="red"
                total={lottiScaduti.length}
                selected={selScaduteVisibili.length}
                onToggleAll={() => toggleTutte(setSelScadute, lottiScaduti, selScaduteVisibili.length === lottiScaduti.length)}
                onAction={eliminaSelezionate}
                actionLabel="Elimina selezionate"
                ActionIcon={Trash2}
                busy={azioneMultipla}
                testidPrefix="etichette-scadute"
              />
              {/* Mobile Cards - Scadute */}
              <div className="lg:hidden space-y-2">
                {lottiScaduti.map((lotto) => {
                  const scadenzaInfo = getScadenzaAlert(lotto.data_scadenza);
                  const selezionato = selScadute.includes(lotto.id);
                  return (
                  <div 
                    key={lotto.id} 
                    data-testid={`lotto-scaduto-card-${lotto.id}`}
                    className={`bg-red-900/30 backdrop-blur-md rounded-xl p-3 border active:scale-[0.99] transition-transform ${selezionato ? 'border-red-400 ring-2 ring-red-500/60' : 'border-red-500/50'}`}
                    onClick={() => showDettaglio(lotto)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <SelezioneCheckbox
                          tone="red"
                          checked={selezionato}
                          onToggle={() => toggleSel(setSelScadute, lotto.id)}
                          testid={`lotto-scaduto-checkbox-${lotto.id}`}
                        />
                        <span className="flex items-center justify-center px-2 py-1 rounded-lg text-xs font-bold border bg-red-600/30 text-red-300 border-red-500/50">
                          {lotto.numero_lotto} ⚠️
                        </span>
                      </div>
                    </div>
                    <p className="font-medium text-white text-sm mb-2">{lotto.ricetta_nome}<BadgeRichiamo lotto={lotto} /></p>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex gap-3 text-slate-400">
                        <span>Prod: {lotto.data_produzione}</span>
                        <span className="text-red-400 font-bold">
                          Scad: {lotto.data_scadenza} (da {Math.abs(scadenzaInfo.giorni)}gg)
                        </span>
                      </div>
                      <span className="text-slate-500">{lotto.quantita} {lotto.unita_misura}</span>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-red-700/50" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => showDettaglio(lotto)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs font-medium">
                        <Eye className="h-3.5 w-3.5" /> Dettagli
                      </button>
                      <button onClick={() => eliminaLotto(lotto.id, lotto.numero_lotto)} data-testid={`lotto-scaduto-elimina-${lotto.id}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-xs font-medium">
                        <Trash2 className="h-3.5 w-3.5" /> Elimina
                      </button>
                    </div>
                  </div>
                )})}
              </div>
              
              {/* Desktop Table - Scadute */}
              <div className="hidden lg:block overflow-x-auto bg-white rounded-xl border border-red-200">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100 bg-red-50">
                      <th className="pl-4 py-3 w-8"></th>
                      <th className="px-4 py-3 font-medium">Lotto</th>
                      <th className="py-3 font-medium">Prodotto</th>
                      <th className="py-3 font-medium">Data prod.</th>
                      <th className="py-3 font-medium">Scadenza</th>
                      <th className="py-3 font-medium">Qtà</th>
                      <th className="py-3 font-medium w-32">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {lottiScaduti.map((lotto) => {
                      const scadenzaInfo = getScadenzaAlert(lotto.data_scadenza);
                      return (
                      <tr key={lotto.id} className={`cursor-pointer transition-colors ${selScadute.includes(lotto.id) ? 'bg-red-50' : 'hover:bg-red-50/50 opacity-75'}`} onClick={() => showDettaglio(lotto)}>
                        <td className="pl-4 py-3">
                          <SelezioneCheckbox
                            tone="red"
                            checked={selScadute.includes(lotto.id)}
                            onToggle={() => toggleSel(setSelScadute, lotto.id)}
                            testid={`lotto-scaduto-checkbox-desktop-${lotto.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700">
                            {lotto.numero_lotto} ⚠️
                          </span>
                        </td>
                        <td className="py-3 font-medium text-gray-900">{lotto.ricetta_nome}<BadgeRichiamo lotto={lotto} /></td>
                        <td className="py-3 text-sm text-gray-600">{lotto.data_produzione}</td>
                        <td className="py-3 text-sm text-red-600 font-bold">
                          {lotto.data_scadenza} (da {Math.abs(scadenzaInfo.giorni)}gg)
                        </td>
                        <td className="py-3 text-sm text-gray-600">{lotto.quantita} {lotto.unita_misura}</td>
                        <td className="py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <button onClick={() => showDettaglio(lotto)} className="p-1.5 hover:bg-gray-100 rounded" title="Dettagli"><Eye className="h-4 w-4 text-gray-500" /></button>
                            <button onClick={() => eliminaLotto(lotto.id, lotto.numero_lotto)} data-testid={`lotto-scaduto-elimina-desktop-${lotto.id}`} className="p-1.5 hover:bg-red-100 rounded" title="Elimina"><Trash2 className="h-4 w-4 text-red-600" /></button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal: QR Code */}
      {showQrModal && selectedLotto && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110] p-4">
          <div className="bg-black/60 backdrop-blur-md lg:bg-white/90 backdrop-blur-sm rounded-2xl w-full max-w-sm overflow-hidden border border-slate-700 lg:border-transparent">
            <div className="p-4 border-b border-slate-700/50 lg:border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-white lg:text-gray-900">QR Code - {selectedLotto.numero_lotto}</h3>
              <button onClick={() => setShowQrModal(false)} className="p-1.5 bg-black/40 backdrop-blur-md lg:bg-transparent hover:bg-slate-700 lg:hover:bg-gray-100 rounded-lg">
                <X className="h-4 w-4 text-slate-400 lg:text-gray-500" />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl">
                <QRCodeSVG 
                  value={(() => {
                    const tenantId = (getTenantDb() || '').replace('tenant_', '');
                    return `${window.location.origin}/api/gestione/haccp/visualizza-lotto/${selectedLotto.id}?tenant=${tenantId}`;
                  })()}
                  size={176}
                  level="M"
                  includeMargin={true}
                />
              </div>
              <p className="mt-4 text-sm text-slate-400 lg:text-gray-600 text-center">
                Scansiona per vedere i dettagli del lotto
              </p>
              <p className="mt-2 font-bold text-blue-400 lg:text-blue-600">{selectedLotto.ricetta_nome}</p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => downloadPdfEtichetta(selectedLotto.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Stampa Etichetta
                </button>
                <button
                  onClick={() => {
                    const tenantId = (getTenantDb() || '').replace('tenant_', '');
                    const url = `${window.location.origin}/api/gestione/haccp/visualizza-lotto/${selectedLotto.id}?tenant=${tenantId}`;
                    window.open(url, '_blank');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Visualizza Contenuto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Dettaglio Lotto - FULLSCREEN su mobile */}
      {showDettaglioModal && selectedLotto && (
        <div data-testid="lotto-dettaglio-modal" className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[100]">
          {/* Mobile Background Image */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-2xl lg:max-h-[90vh] overflow-hidden flex flex-col relative z-10">
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-blue-50 lg:to-purple-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowDettaglioModal(false)} className="p-2 bg-black/40 backdrop-blur-md lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/70 rounded-xl">
                  <ChevronLeft className="h-5 w-5 text-white lg:text-gray-500" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Lotto {selectedLotto.numero_lotto}</h3>
                  <p className="text-xs text-blue-400 lg:text-blue-600">{selectedLotto.ricetta_nome}</p>
                </div>
              </div>
              <button onClick={() => setShowDettaglioModal(false)} className="p-2 bg-black/40 backdrop-blur-md lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/70 rounded-xl hidden lg:block">
                <X className="h-4 w-4 text-white lg:text-gray-500" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32 lg:pb-4">
              {/* Dati Produzione */}
              <div className="bg-black/40 backdrop-blur-md lg:bg-gray-50 rounded-xl p-4 border border-slate-700 lg:border-transparent">
                <h4 className="font-semibold text-white lg:text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-400 lg:text-blue-600" />
                  Dati Produzione
                </h4>
                {!editMode ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-400 lg:text-gray-500">Data produzione:</span> <span className="font-medium text-white lg:text-gray-900">{selectedLotto.data_produzione}</span></div>
                    <div><span className="text-slate-400 lg:text-gray-500">Data scadenza:</span> <span className="font-medium text-white lg:text-gray-900">{selectedLotto.data_scadenza}</span></div>
                    <div><span className="text-slate-400 lg:text-gray-500">Quantità:</span> <span className="font-medium text-white lg:text-gray-900">{selectedLotto.quantita} {selectedLotto.unita_misura}</span></div>
                    <div><span className="text-slate-400 lg:text-gray-500">Conservazione:</span> <span className="font-medium text-white lg:text-gray-900">{selectedLotto.abbattimento ? (selectedLotto.metodo_conservazione || 'Congelato') : 'Refrigerato'}</span></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex flex-col items-center">
                      <label className="text-xs text-slate-400 mb-1">Data produzione</label>
                      <input
                        type="date"
                        data-testid="lotto-edit-data-produzione"
                        value={editingLotto?.data_produzione || ''}
                        onChange={(e) => setEditingLotto({...editingLotto, data_produzione: e.target.value})}
                        className="px-3 py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-sm text-white lg:text-gray-900"
                      />
                    </div>
                    <div className="flex flex-col items-center">
                      <label className="text-xs text-slate-400 mb-1">Data scadenza</label>
                      <input
                        type="date"
                        data-testid="lotto-edit-data-scadenza"
                        value={editingLotto?.data_scadenza || ''}
                        min={editingLotto?.data_produzione || undefined}
                        onChange={(e) => setEditingLotto({...editingLotto, data_scadenza: e.target.value})}
                        className="px-3 py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-sm text-white lg:text-gray-900"
                      />
                      <p className="text-[11px] text-slate-400 lg:text-gray-500 mt-1">Non cambia da sola se modifichi la produzione</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <label className="text-xs text-slate-400 mb-1">Quantità</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={editingLotto?.quantita || ''}
                          onChange={(e) => setEditingLotto({...editingLotto, quantita: e.target.value})}
                          className="w-20 px-3 py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-sm text-white lg:text-gray-900 text-center"
                        />
                        <select
                          value={editingLotto?.unita_misura || 'porzioni'}
                          onChange={(e) => setEditingLotto({...editingLotto, unita_misura: e.target.value})}
                          className="w-28 px-2 py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-sm text-white lg:text-gray-900"
                        >
                          <option value="porzioni">Porzioni</option>
                          <option value="kg">Kg</option>
                          <option value="pz">Pz</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Scheda di Conservazione - View/Edit */}
              {(!editMode && selectedLotto.abbattimento) && (
                <div className="bg-blue-500/10 lg:bg-blue-50 rounded-xl p-4 border border-blue-500/30 lg:border-blue-200">
                  <h4 className="font-semibold text-blue-400 lg:text-blue-900 mb-3">📋 Scheda di Conservazione</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedLotto.metodo_conservazione && (
                      <div className="col-span-2"><span className="text-blue-300 lg:text-blue-700">Metodo:</span> <span className="font-medium text-white lg:text-blue-900 capitalize">{selectedLotto.metodo_conservazione}</span></div>
                    )}
                    <div><span className="text-blue-300 lg:text-blue-700">Inizio:</span> <span className="font-medium text-white lg:text-blue-900">{selectedLotto.abbattimento_inizio_data} {selectedLotto.abbattimento_inizio_ora}</span></div>
                    <div><span className="text-blue-300 lg:text-blue-700">Fine:</span> <span className="font-medium text-white lg:text-blue-900">{selectedLotto.abbattimento_fine_data} {selectedLotto.abbattimento_fine_ora}</span></div>
                    <div><span className="text-blue-300 lg:text-blue-700">Temperatura:</span> <span className="font-medium text-white lg:text-blue-900">{selectedLotto.temperatura_abbattimento}°C</span></div>
                    {selectedLotto.eviscerato !== undefined && (
                      <div><span className="text-blue-300 lg:text-blue-700">Eviscerato:</span> <span className="font-medium text-white lg:text-blue-900">{selectedLotto.eviscerato ? 'Sì' : 'No'}</span></div>
                    )}
                    {selectedLotto.presenza_vermi !== undefined && (
                      <div><span className="text-blue-300 lg:text-blue-700">Presenza vermi:</span> <span className="font-medium text-white lg:text-blue-900">{selectedLotto.presenza_vermi ? 'Sì' : 'No'}</span></div>
                    )}
                  </div>
                </div>
              )}

              {/* Scheda di Conservazione - Edit Mode */}
              {editMode && (
                <div className="bg-blue-500/10 lg:bg-blue-50 rounded-xl p-3 space-y-3 border border-blue-500/30 lg:border-blue-200">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-blue-400 lg:text-blue-900 text-sm">📋 Scheda di Conservazione</h4>
                    <button
                      onClick={() => setEditingLotto({...editingLotto, abbattimento: !editingLotto?.abbattimento})}
                      className={`w-11 h-6 rounded-full transition-colors ${editingLotto?.abbattimento ? 'bg-blue-500' : 'bg-slate-600'}`}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${editingLotto?.abbattimento ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  
                  {editingLotto?.abbattimento && (
                    <>
                      {/* Toggle Eviscerato e Presenza vermi */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-2 bg-slate-800/40 backdrop-blur-md rounded-lg">
                          <span className="text-sm text-white">Eviscerato</span>
                          <button
                            onClick={() => setEditingLotto({...editingLotto, eviscerato: !editingLotto?.eviscerato})}
                            className={`w-10 h-5 rounded-full transition-colors ${editingLotto?.eviscerato ? 'bg-green-500' : 'bg-slate-600'}`}
                          >
                            <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${editingLotto?.eviscerato ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-slate-800/40 backdrop-blur-md rounded-lg">
                          <span className="text-sm text-white">Presenza di vermi</span>
                          <button
                            onClick={() => setEditingLotto({...editingLotto, presenza_vermi: !editingLotto?.presenza_vermi})}
                            className={`w-10 h-5 rounded-full transition-colors ${editingLotto?.presenza_vermi ? 'bg-red-500' : 'bg-slate-600'}`}
                          >
                            <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${editingLotto?.presenza_vermi ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Date/Ora Inizio/Fine */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col items-center space-y-2">
                          <p className="text-xs text-blue-300 font-medium">Inizio</p>
                          <input
                            type="date"
                            value={editingLotto?.abbattimento_inizio_data || ''}
                            onChange={(e) => setEditingLotto({...editingLotto, abbattimento_inizio_data: e.target.value})}
                            className="px-2 py-1.5 rounded-lg border border-blue-500/30 text-xs bg-slate-700 text-white"
                          />
                          <input
                            type="time"
                            value={editingLotto?.abbattimento_inizio_ora || ''}
                            onChange={(e) => setEditingLotto({...editingLotto, abbattimento_inizio_ora: e.target.value})}
                            className="px-2 py-1.5 rounded-lg border border-blue-500/30 text-xs bg-slate-700 text-white"
                          />
                        </div>
                        <div className="flex flex-col items-center space-y-2">
                          <p className="text-xs text-blue-300 font-medium">Fine</p>
                          <input
                            type="date"
                            value={editingLotto?.abbattimento_fine_data || ''}
                            onChange={(e) => setEditingLotto({...editingLotto, abbattimento_fine_data: e.target.value})}
                            className="px-2 py-1.5 rounded-lg border border-blue-500/30 text-xs bg-slate-700 text-white"
                          />
                          <input
                            type="time"
                            value={editingLotto?.abbattimento_fine_ora || ''}
                            onChange={(e) => setEditingLotto({...editingLotto, abbattimento_fine_ora: e.target.value})}
                            className="px-2 py-1.5 rounded-lg border border-blue-500/30 text-xs bg-slate-700 text-white"
                          />
                        </div>
                      </div>
                      
                      {/* Temperatura */}
                      <div className="flex flex-col items-center">
                        <label className="text-xs text-blue-300 mb-1">Temperatura (°C)</label>
                        <input
                          type="number"
                          value={editingLotto?.temperatura_abbattimento || -18}
                          onChange={(e) => setEditingLotto({...editingLotto, temperatura_abbattimento: e.target.value})}
                          className="w-20 px-3 py-2 rounded-lg border border-blue-500/30 text-sm bg-slate-700 text-white text-center"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Ingredienti */}
              <div className="bg-black/40 backdrop-blur-md lg:bg-gray-50 rounded-xl p-4 border border-slate-700 lg:border-transparent">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-white lg:text-gray-900 flex items-center gap-2">
                    <Package className="h-4 w-4 text-green-400 lg:text-green-600" />
                    Ingredienti ({selectedLotto.ingredienti_riconciliati?.length || 0})
                  </h4>
                  {puoModificare && (
                    <button
                      onClick={() => openAddIngredienteModal(true)}
                      data-testid="lotto-aggiungi-ingrediente-btn"
                      className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" />
                      Aggiungi
                    </button>
                  )}
                </div>
                <div className="space-y-2 max-h-[45vh] lg:max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {(selectedLotto.ingredienti_riconciliati || []).map((ing, idx) => (
                    <div key={idx} className="p-3 bg-slate-700/50 lg:bg-white/90 backdrop-blur-sm rounded-lg border border-slate-600 lg:border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-white lg:text-gray-900 text-sm">
                            {ing.nome_prodotto_ddt || ing.nome_ingrediente}
                          </p>
                          {ing.nome_prodotto_ddt && ing.nome_ingrediente && ing.nome_prodotto_ddt !== ing.nome_ingrediente && (
                            <p className="text-xs text-slate-500 lg:text-gray-400">Ingrediente ricetta: {ing.nome_ingrediente}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {puoModificare && (
                            <button
                              onClick={() => cercaStoricoDdt(ing, idx)}
                              data-testid={`lotto-ingrediente-cambia-${idx}`}
                              className="text-xs px-2 py-1 bg-purple-500/20 lg:bg-purple-100 text-purple-400 lg:text-purple-700 rounded-lg hover:bg-purple-500/30 lg:hover:bg-purple-200 flex items-center gap-1"
                              title="Cambia DDT/Lotto"
                            >
                              <RefreshCw className="h-3 w-3" />
                              Cambia
                            </button>
                          )}
                          {ing.ddt_id && (
                            <button
                              onClick={() => apriDDT(ing.ddt_id)}
                              className="text-xs px-2 py-1 bg-blue-500/20 lg:bg-blue-100 text-blue-400 lg:text-blue-700 rounded-lg hover:bg-blue-500/30 lg:hover:bg-blue-200 flex items-center gap-1"
                            >
                              <FileText className="h-3 w-3" />
                              DDT
                            </button>
                          )}
                          {puoModificare && (
                            <button
                              onClick={() => rimuoviIngredienteDaLotto(idx)}
                              data-testid={`lotto-ingrediente-rimuovi-${idx}`}
                              className="p-1 text-red-400 hover:text-red-500 hover:bg-red-500/20 lg:hover:bg-red-50 rounded"
                              title="Rimuovi"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500 lg:text-gray-500">Fornitore:</span>
                          <span className="font-medium text-slate-300 lg:text-gray-700">{ing.fornitore || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500 lg:text-gray-500">Lotto prodotto:</span>
                          <span className="font-medium text-slate-300 lg:text-gray-700">{ing.numero_lotto || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500 lg:text-gray-500">DDT n°:</span>
                          <span className="font-medium text-slate-300 lg:text-gray-700">{ing.ddt_numero || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500 lg:text-gray-500">Data DDT:</span>
                          <span className="font-medium text-slate-300 lg:text-gray-700">{ing.data_ddt || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!selectedLotto.ingredienti_riconciliati || selectedLotto.ingredienti_riconciliati.length === 0) && (
                    <p className="text-sm text-slate-500 lg:text-gray-500 text-center py-4">Nessun ingrediente registrato</p>
                  )}
                </div>
              </div>

              {/* Allergeni */}
              <div className="bg-orange-500/10 lg:bg-orange-50 rounded-xl p-4 border border-orange-500/30 lg:border-orange-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-orange-400 lg:text-orange-900 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Allergeni
                  </h4>
                  {puoModificare && (
                    <button
                      onClick={() => analizzaAllergeni(selectedLotto.id)}
                      disabled={analizzandoAllergeni}
                      data-testid="lotto-ricalcola-allergeni-btn"
                      className="text-xs px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                    >
                      {analizzandoAllergeni ? 'Analisi...' : 'Ricalcola AI'}
                    </button>
                  )}
                </div>
                {/* Mostra allergeni da allergeni_totali O allergeni (array) */}
                {(() => {
                  // Normalizza gli allergeni in un array
                  let allergeniArray = [];
                  if (Array.isArray(selectedLotto.allergeni_totali) && selectedLotto.allergeni_totali.length > 0) {
                    allergeniArray = selectedLotto.allergeni_totali;
                  } else if (Array.isArray(selectedLotto.allergeni) && selectedLotto.allergeni.length > 0) {
                    allergeniArray = selectedLotto.allergeni;
                  }
                  
                  return allergeniArray.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {allergeniArray.map((all, idx) => (
                        <span key={idx} className="px-2 py-1 bg-orange-500/20 lg:bg-orange-100 text-orange-300 lg:text-orange-800 rounded-lg text-xs font-medium border border-orange-500/30 lg:border-transparent">
                          {all}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-orange-300 lg:text-orange-700">
                      {analizzandoAllergeni ? 'Analisi in corso...' : 'Clicca "Ricalcola AI" per analizzare gli allergeni'}
                    </p>
                  );
                })()}
                {selectedLotto.allergeni_analisi?.allergeni_possibili?.length > 0 && (
                  <p className="mt-2 text-xs text-orange-400 lg:text-orange-600">
                    Può contenere tracce di: {selectedLotto.allergeni_analisi.allergeni_possibili.join(', ')}
                  </p>
                )}
              </div>
            </div>

            {/* Footer con bottoni - FISSO in basso su mobile SOPRA la bottom nav */}
            {!editMode ? (
              <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-3 border-t border-slate-700/50 lg:border-gray-100 flex gap-2 flex-shrink-0 bg-black/60 backdrop-blur-md lg:bg-white z-[101]">
                {puoModificare ? (
                  <button
                    onClick={startEditLotto}
                    data-testid="lotto-modifica-btn"
                    className="px-4 py-3 lg:py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-1"
                    title="Modifica date e conservazione"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                ) : (
                  <span data-testid="lotto-solo-responsabile-nota" className="self-center text-[11px] leading-tight text-slate-400 lg:text-gray-500 max-w-[5.5rem]">
                    Modifica: solo il responsabile
                  </span>
                )}
                <button
                  onClick={() => downloadSchedaPdf(selectedLotto.id)}
                  className="flex-1 py-3 lg:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-1"
                >
                  <Download className="h-4 w-4" />
                  PDF
                </button>
                <button
                  onClick={() => showQr(selectedLotto)}
                  className="px-4 py-3 lg:py-2.5 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium text-sm transition-colors border border-slate-700 lg:border-transparent"
                  title="QR Code"
                >
                  <QrCode className="h-4 w-4" />
                </button>
                <button
                  onClick={() => downloadPdfEtichetta(selectedLotto.id)}
                  className="flex-1 py-3 lg:py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-1"
                  title="Stampa Etichetta"
                >
                  <Printer className="h-4 w-4" />
                  Stampa
                </button>
                <button
                  onClick={() => eliminaLotto(selectedLotto.id, selectedLotto.numero_lotto)}
                  className="px-4 py-3 lg:py-2.5 bg-red-500/20 lg:bg-red-100 hover:bg-red-500/30 lg:hover:bg-red-200 text-red-400 lg:text-red-700 rounded-xl font-medium text-sm transition-colors border border-red-500/30 lg:border-transparent"
                  title="Elimina lotto"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-3 border-t border-slate-700/50 lg:border-gray-100 flex gap-2 flex-shrink-0 bg-black/60 backdrop-blur-md lg:bg-white z-[101]">
                <button
                  onClick={cancelEditLotto}
                  data-testid="lotto-annulla-modifica-btn"
                  className="flex-1 py-3 lg:py-2.5 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium text-sm transition-colors border border-slate-700 lg:border-transparent"
                >
                  Annulla
                </button>
                <button
                  onClick={saveEditLotto}
                  disabled={savingEdit}
                  data-testid="lotto-salva-modifiche-btn"
                  className="flex-1 py-3 lg:py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {savingEdit ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Salva Modifiche
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Scegli tipo */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-black/60 backdrop-blur-md lg:bg-white/90 backdrop-blur-sm rounded-2xl w-full max-w-sm overflow-hidden border border-slate-700 lg:border-transparent">
            <div className="p-4 border-b border-slate-700/50 lg:border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-white lg:text-gray-900">Nuovo Lotto Produzione</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 bg-black/40 backdrop-blur-md lg:bg-transparent hover:bg-slate-700 lg:hover:bg-gray-100 rounded-lg">
                <X className="h-4 w-4 text-slate-400 lg:text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {/* Importa da ricetta esistente */}
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowImportModal(true);
                  loadRicette(); // Carica ricette quando si apre il modal
                }}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-slate-700/50 lg:border-gray-200 bg-black/40 backdrop-blur-md lg:bg-white hover:border-blue-500 lg:hover:border-blue-300 hover:bg-slate-700 lg:hover:bg-blue-50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-600/20 lg:bg-blue-100 flex items-center justify-center">
                  <Import className="h-6 w-6 text-blue-400 lg:text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-white lg:text-gray-900">Importa da ricetta</p>
                  <p className="text-sm text-slate-400 lg:text-gray-500">Crea lotto da ricetta esistente</p>
                </div>
              </button>
              
              {/* NUOVO: Crea Ricetta manualmente */}
              <button
                onClick={openCreaRicettaModal}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-purple-500/30 lg:border-purple-200 bg-purple-600/10 lg:bg-purple-50 hover:border-purple-500 lg:hover:border-purple-400 hover:bg-purple-600/20 lg:hover:bg-purple-100 transition-colors"
                data-testid="crea-ricetta-btn"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-600/20 lg:bg-purple-100 flex items-center justify-center">
                  <Plus className="h-6 w-6 text-purple-400 lg:text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-white lg:text-gray-900">Crea Ricetta</p>
                  <p className="text-sm text-slate-400 lg:text-gray-500">Nuovo lotto con ingredienti manuali</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Importa da ricetta - Wizard FULLSCREEN su mobile */}
      {showImportModal && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[100]">
          {/* Mobile Background Image */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-2xl h-full lg:h-auto lg:max-h-[95vh] overflow-hidden flex flex-col relative z-10" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            {/* Header */}
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between flex-shrink-0 bg-transparent lg:bg-gradient-to-r lg:from-blue-50 lg:to-purple-50" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                {importStep > 1 ? (
                  <button 
                    onClick={() => setImportStep(importStep - 1)}
                    className="p-2 bg-black/40 backdrop-blur-md lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/70 rounded-xl text-white lg:text-gray-500"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                ) : (
                  <button 
                    onClick={closeImportModal}
                    className="p-2 bg-black/40 backdrop-blur-md lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/70 rounded-xl text-white lg:text-gray-500"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900 text-base">
                    {importStep === 1 && 'Seleziona Ricetta'}
                    {importStep === 2 && 'Preparazione Lotto'}
                    {importStep === 3 && 'Scheda di Conservazione'}
                  </h3>
                  <p className="text-xs text-slate-400 lg:text-gray-500">
                    Step {importStep}/3 {numeroLotto && `• ${numeroLotto}`}
                  </p>
                </div>
              </div>
              <button onClick={closeImportModal} className="p-2 bg-black/40 backdrop-blur-md lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/70 rounded-xl hidden lg:block">
                <X className="h-4 w-4 text-white lg:text-gray-500" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-slate-700 lg:bg-gray-200 flex-shrink-0">
              <div 
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${(importStep / 3) * 100}%` }}
              />
            </div>

            {/* Step 1: Selezione Ricetta */}
            {importStep === 1 && (
              <div className="flex-1 overflow-y-auto p-4">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca ricetta..."
                    value={ricettaSearch}
                    onChange={(e) => setRicettaSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-black/40 backdrop-blur-md lg:bg-white border-slate-700/50 lg:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-white lg:text-gray-900 placeholder-slate-400"
                  />
                </div>
                <div className="space-y-2">
                  {filteredRicette.length === 0 ? (
                    <p className="text-center text-slate-400 lg:text-gray-500 py-8">Nessuna ricetta trovata</p>
                  ) : (
                    filteredRicette.map((ricetta) => (
                      <button
                        key={ricetta.id}
                        onClick={() => handleSelectRicetta(ricetta)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-700/50 lg:border-gray-200 bg-black/40 backdrop-blur-md lg:bg-white hover:border-blue-500 lg:hover:border-blue-300 hover:bg-slate-700 lg:hover:bg-blue-50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-orange-500/20 lg:bg-orange-100 flex items-center justify-center text-lg">
                          🍽️
                        </div>
                        <div className="text-left flex-1">
                          <p className="font-medium text-white lg:text-gray-900 text-sm">{ricetta.nome}</p>
                          <p className="text-xs text-slate-400 lg:text-gray-500">{ricetta.ingredienti?.length || 0} ingredienti</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-500 lg:text-gray-400" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Preparazione Lotto + Ingredienti */}
            {importStep === 2 && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Info ricetta selezionata */}
                <div className="bg-blue-600/20 lg:bg-blue-50 rounded-xl p-3 flex items-center gap-3 border border-blue-500/30 lg:border-transparent">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 lg:bg-orange-100 flex items-center justify-center text-lg">🍽️</div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={selectedRicetta?.nome || ''}
                      onChange={(e) => setSelectedRicetta({ ...selectedRicetta, nome: e.target.value })}
                      data-testid="import-nome-ricetta-input"
                      title="Puoi modificare il nome che apparirà sull'etichetta"
                      className="w-full font-medium text-white lg:text-gray-900 text-sm bg-transparent border-b border-dashed border-blue-400/50 lg:border-blue-300 focus:border-blue-400 focus:outline-none py-0.5"
                    />
                    <p className="text-xs text-blue-400 lg:text-blue-600 mt-1">Lotto: {numeroLotto} · ✏️ nome modificabile</p>
                  </div>
                </div>

                {/* Form preparazione - COMPATTO su mobile */}
                <div className="bg-black/40 backdrop-blur-md lg:bg-gray-50 rounded-xl p-3 lg:p-4 space-y-2 lg:space-y-3 border border-slate-700 lg:border-transparent">
                  <h4 className="font-semibold text-white lg:text-gray-900 text-sm flex items-center justify-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-400 lg:text-blue-600" />
                    Dati Produzione
                  </h4>
                  {/* Grid layout per mobile: data e quantità sulla stessa riga */}
                  <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 lg:gap-3">
                    <div className="flex flex-col items-center">
                      <label className="block text-xs text-slate-400 lg:text-gray-500 mb-1">Data produzione</label>
                      <input
                        type="date"
                        value={lottoForm.data_produzione}
                        onChange={(e) => setLottoForm({...lottoForm, data_produzione: e.target.value})}
                        className="w-full px-2 lg:px-4 py-1.5 lg:py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-xs lg:text-sm text-white lg:text-gray-900"
                      />
                    </div>
                    <div className="flex flex-col items-center">
                      <label className="block text-xs text-slate-400 lg:text-gray-500 mb-1">Quantità</label>
                      <div className="flex gap-1 lg:gap-2 justify-center w-full">
                        <input
                          type="number"
                          value={lottoForm.quantita}
                          onChange={(e) => setLottoForm({...lottoForm, quantita: e.target.value})}
                          className="w-12 lg:w-20 px-2 lg:px-3 py-1.5 lg:py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-xs lg:text-sm text-white lg:text-gray-900 text-center"
                        />
                        <select
                          value={lottoForm.unita_misura}
                          onChange={(e) => setLottoForm({...lottoForm, unita_misura: e.target.value})}
                          className="flex-1 px-1 lg:px-2 py-1.5 lg:py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-xs lg:text-sm text-white lg:text-gray-900 text-center"
                        >
                          <option value="porzioni">Porzioni</option>
                          <option value="kg">Kg</option>
                          <option value="pz">Pz</option>
                        </select>
                      </div>
                    </div>
                  </div>
                    
                  {/* Scadenza - Bottoni più piccoli su mobile */}
                  <div className="flex flex-col items-center">
                      <label className="block text-xs text-slate-400 lg:text-gray-500 mb-1 lg:mb-2">Scadenza (giorni)</label>
                      <div className="flex flex-wrap justify-center gap-1 lg:gap-2 mb-1 lg:mb-2">
                        {['30', '60', '90', '120'].map((giorni) => (
                          <button
                            key={giorni}
                            type="button"
                            onClick={() => setLottoForm({
                              ...lottoForm, 
                              tipo_scadenza: lottoForm.tipo_scadenza === giorni ? '' : giorni, 
                              scadenza_custom: ''
                            })}
                            className={`px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-colors ${
                              lottoForm.tipo_scadenza === giorni
                                ? 'bg-cyan-600 text-white'
                                : 'bg-slate-700 lg:bg-gray-200 text-slate-300 lg:text-gray-700 hover:bg-slate-600 lg:hover:bg-gray-300'
                            }`}
                          >
                            {giorni}gg
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setLottoForm({
                            ...lottoForm, 
                            tipo_scadenza: lottoForm.tipo_scadenza === 'custom' ? '' : 'custom'
                          })}
                          className={`px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-colors ${
                            lottoForm.tipo_scadenza === 'custom'
                              ? 'bg-cyan-600 text-white'
                              : 'bg-slate-700 lg:bg-gray-200 text-slate-300 lg:text-gray-700 hover:bg-slate-600 lg:hover:bg-gray-300'
                          }`}
                        >
                          Shelf Life
                        </button>
                      </div>
                      {lottoForm.tipo_scadenza === 'custom' && (
                        <div className="w-full flex justify-center">
                          <input
                            type="date"
                            value={lottoForm.scadenza_custom}
                            onChange={(e) => setLottoForm({...lottoForm, scadenza_custom: e.target.value})}
                            className="px-3 lg:px-4 py-1.5 lg:py-2 rounded-lg border bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-xs lg:text-sm text-white lg:text-gray-900"
                            placeholder="Data scadenza personalizzata"
                          />
                        </div>
                      )}
                      {lottoForm.tipo_scadenza && lottoForm.tipo_scadenza !== 'custom' && (
                        <p className="text-xs text-slate-500 lg:text-gray-400 text-center">
                          Scadenza: {(() => {
                            const dataProd = new Date(lottoForm.data_produzione);
                            const giorni = parseInt(lottoForm.tipo_scadenza);
                            const dataScad = new Date(dataProd);
                            dataScad.setDate(dataScad.getDate() + giorni);
                            return `${String(dataScad.getDate()).padStart(2, '0')}/${String(dataScad.getMonth() + 1).padStart(2, '0')}/${dataScad.getFullYear()}`;
                          })()}
                        </p>
                      )}
                      {!lottoForm.tipo_scadenza && (
                        <p className="text-xs text-yellow-500 lg:text-yellow-600 text-center">
                          ⚠️ Senza selezione, la scadenza sarà a 3 giorni dalla produzione
                        </p>
                      )}
                    </div>
                </div>

                {/* Lista ingredienti da riconciliare */}
                <div className="bg-black/40 backdrop-blur-md lg:bg-gray-50 rounded-xl p-4 space-y-3 border border-slate-700 lg:border-transparent">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-white lg:text-gray-900 text-sm flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-green-400 lg:text-green-600" />
                      Riconcilia Ingredienti
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 lg:text-gray-500">
                        {ingredientiRiconciliatiCount}/{ingredientiRiconciliati.length}
                      </span>
                      <button
                        onClick={() => openAddIngredienteModal(false)}
                        className="text-xs px-2 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Aggiungi
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 lg:text-gray-500">
                    Clicca su ogni ingrediente per abbinarlo al DDT
                  </p>
                  <div className="space-y-2 max-h-[40vh] lg:max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    {ingredientiRiconciliati.map((ing, ingIdx) => (
                      <div
                        key={ing.id}
                        className={`w-full flex items-center gap-2 sm:gap-3 p-3 sm:p-3 rounded-lg border transition-colors min-h-[56px] ${
                          ing.riconciliato 
                            ? 'border-green-500/50 bg-green-500/10 lg:border-green-300 lg:bg-green-50' 
                            : 'border-slate-600 lg:border-gray-200 hover:border-blue-500 lg:hover:border-blue-300 hover:bg-slate-700 lg:hover:bg-blue-50'
                        }`}
                      >
                        <button
                          onClick={() => openRiconciliaModal(ing)}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        >
                          <div className={`w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center ${
                            ing.riconciliato ? 'bg-green-500/20 lg:bg-green-100' : 'bg-slate-700 lg:bg-gray-100'
                          }`}>
                            {ing.riconciliato ? (
                              <Check className="h-4 w-4 text-green-400 lg:text-green-600" />
                            ) : (
                              <Link2 className="h-4 w-4 text-slate-400 lg:text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {editingIngredienteId === ing.id ? (
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editingIngredienteNome}
                                  onChange={(e) => setEditingIngredienteNome(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEditIngrediente(ing.id);
                                    if (e.key === 'Escape') cancelEditIngrediente();
                                  }}
                                  className="flex-1 px-2 py-1 text-sm rounded border bg-white lg:bg-white text-gray-900 border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                  autoFocus
                                />
                                <button
                                  onClick={(e) => { e.stopPropagation(); saveEditIngrediente(ing.id); }}
                                  className="p-1 text-green-500 hover:bg-green-500/20 rounded"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); cancelEditIngrediente(); }}
                                  className="p-1 text-red-500 hover:bg-red-500/20 rounded"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <p className="font-medium text-white lg:text-gray-900 text-sm">{ing.nome_ingrediente}</p>
                                {ing.riconciliato ? (
                                  <p className={`text-xs truncate ${ing.tipo_fonte === 'interno' ? 'text-blue-400 lg:text-blue-600' : 'text-green-400 lg:text-green-600'}`}>
                                    {ing.tipo_fonte === 'interno' ? '🏭' : '✓'} {ing.fornitore} - Lotto: {ing.numero_lotto || 'N/D'}
                                  </p>
                                ) : (
                                  <p className="text-xs text-slate-400 lg:text-gray-400">Clicca per riconciliare</p>
                                )}
                              </>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-500 lg:text-gray-400 flex-shrink-0" />
                        </button>
                        {/* Bottoni Edit e Delete */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditIngrediente(ing);
                          }}
                          className="flex-shrink-0 p-1.5 text-cyan-400 hover:text-cyan-500 hover:bg-cyan-500/20 lg:hover:bg-cyan-100 rounded-lg transition-colors"
                          title="Modifica nome"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            rimuoviIngrediente(ing.id);
                          }}
                          className="flex-shrink-0 p-1.5 text-red-400 hover:text-red-500 hover:bg-red-500/20 lg:hover:bg-red-100 rounded-lg transition-colors"
                          title="Rimuovi ingrediente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Conservazione */}
            {importStep === 3 && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Riepilogo */}
                <div className="bg-blue-600/20 lg:bg-blue-50 rounded-xl p-3 flex items-center gap-3 border border-blue-500/30 lg:border-transparent">
                  <div className="w-10 h-10 rounded-lg bg-blue-600/30 lg:bg-blue-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-400 lg:text-blue-700">{numeroLotto?.split('/')[0]}</span>
                  </div>
                  <div>
                    <p className="font-medium text-white lg:text-gray-900 text-sm">{selectedRicetta?.nome}</p>
                    <p className="text-xs text-blue-400 lg:text-blue-600">
                      {lottoForm.quantita} {lottoForm.unita_misura} • {ingredientiRiconciliatiCount} ingredienti
                    </p>
                  </div>
                </div>

                {/* Toggle Frazionamento */}
                <div className="flex items-center justify-between p-4 bg-purple-500/10 lg:bg-purple-50 rounded-xl border border-purple-500/30 lg:border-purple-200">
                  <div>
                    <p className="font-medium text-white lg:text-gray-900 text-sm">🔀 Frazionare la ricetta?</p>
                    <p className="text-xs text-purple-400 lg:text-purple-600">Dividi in porzioni con conservazioni diverse</p>
                  </div>
                  <button
                    onClick={() => setFrazionamento({...frazionamento, abilitato: !frazionamento.abilitato})}
                    className={`w-12 h-6 rounded-full transition-colors ${frazionamento.abilitato ? 'bg-purple-500' : 'bg-slate-600 lg:bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${frazionamento.abilitato ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Frazionamento UI */}
                {frazionamento.abilitato && (
                  <div className="bg-purple-500/10 lg:bg-purple-50 rounded-xl p-3 space-y-3 border border-purple-500/30 lg:border-purple-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-purple-400 lg:text-purple-900 text-sm">
                        Frazioni (Totale: {lottoForm.quantita} {lottoForm.unita_misura})
                      </h4>
                      <button
                        onClick={() => setFrazionamento({
                          ...frazionamento,
                          frazioni: [...frazionamento.frazioni, { 
                            quantita: 0, 
                            conservazione: '', 
                            giorni_scadenza: 3,
                            inizio_data: getTodayInput(),
                            inizio_ora: '',
                            fine_data: getTodayInput(),
                            fine_ora: '',
                            temperatura: -18,
                            eviscerato: false,
                            presenza_vermi: false,
                            note_conservazione: ''
                          }]
                        })}
                        className="text-xs bg-purple-500 text-white px-2 py-1 rounded"
                      >
                        + Aggiungi Frazione
                      </button>
                    </div>
                    
                    {/* Info quantità rimanente */}
                    {(() => {
                      const totaleAssegnato = frazionamento.frazioni.reduce((sum, f) => sum + (parseFloat(f.quantita) || 0), 0);
                      const rimanente = (parseFloat(lottoForm.quantita) || 0) - totaleAssegnato;
                      return (
                        <div className={`text-xs text-center p-2 rounded ${rimanente < 0 ? 'bg-red-500/20 text-red-400' : rimanente > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>
                          {rimanente < 0 && `⚠️ Hai assegnato ${Math.abs(rimanente)} ${lottoForm.unita_misura} in più del totale!`}
                          {rimanente > 0 && `📦 Rimangono ${rimanente} ${lottoForm.unita_misura} da assegnare`}
                          {rimanente === 0 && `✅ Tutte le ${lottoForm.quantita} ${lottoForm.unita_misura} sono state assegnate`}
                        </div>
                      );
                    })()}
                    
                    {frazionamento.frazioni.map((fraz, idx) => (
                      <div key={idx} className="bg-slate-800/40 lg:bg-white rounded-lg p-3 space-y-3 border border-slate-600/50 lg:border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white lg:text-gray-900">
                            🏷️ Lotto Frazione {idx + 1}
                          </span>
                          {frazionamento.frazioni.length > 1 && (
                            <button
                              onClick={() => setFrazionamento({
                                ...frazionamento,
                                frazioni: frazionamento.frazioni.filter((_, i) => i !== idx)
                              })}
                              className="text-red-500 text-xs hover:underline"
                            >
                              Rimuovi
                            </button>
                          )}
                        </div>
                        
                        {/* Riga 1: Quantità e Metodo Conservazione */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-slate-400 lg:text-gray-500">Quantità ({lottoForm.unita_misura})</label>
                            <input
                              type="number"
                              min="0"
                              max={lottoForm.quantita}
                              value={fraz.quantita}
                              onChange={(e) => {
                                const newFrazioni = [...frazionamento.frazioni];
                                newFrazioni[idx].quantita = parseFloat(e.target.value) || 0;
                                setFrazionamento({...frazionamento, frazioni: newFrazioni});
                              }}
                              className="w-full px-2 py-1.5 rounded text-sm bg-slate-700 lg:bg-gray-100 text-white lg:text-gray-900"
                              placeholder={`Es: ${Math.floor((parseFloat(lottoForm.quantita) || 0) / frazionamento.frazioni.length)}`}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 lg:text-gray-500">Metodo Conservazione</label>
                            <select
                              value={fraz.conservazione}
                              onChange={(e) => {
                                const newFrazioni = [...frazionamento.frazioni];
                                newFrazioni[idx].conservazione = e.target.value;
                                const metodo = METODI_CONSERVAZIONE.find(m => m.id === e.target.value);
                                newFrazioni[idx].giorni_scadenza = metodo ? metodo.giorni_extra : 3;
                                if (metodo?.temp) newFrazioni[idx].temperatura = metodo.temp;
                                setFrazionamento({...frazionamento, frazioni: newFrazioni});
                              }}
                              className="w-full px-2 py-1.5 rounded text-sm bg-slate-700 lg:bg-gray-100 text-white lg:text-gray-900"
                            >
                              <option value="">🍽️ Nessuna (3gg)</option>
                              {METODI_CONSERVAZIONE.map(m => (
                                <option key={m.id} value={m.id}>{m.icon} {m.label} (+{m.giorni_extra}gg)</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {/* Scheda Conservazione specifica (solo se metodo selezionato) */}
                        {fraz.conservazione && (
                          <div className="bg-blue-500/10 lg:bg-blue-50 rounded-lg p-3 space-y-2 border border-blue-500/30 lg:border-blue-200">
                            <p className="text-xs font-medium text-blue-400 lg:text-blue-800">
                              📋 Scheda {METODI_CONSERVAZIONE.find(m => m.id === fraz.conservazione)?.label || 'Conservazione'}
                            </p>
                            
                            {/* Eviscerato/Vermi solo per abbattimento e congelamento */}
                            {['abbattimento', 'congelamento'].includes(fraz.conservazione) && (
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2 text-xs text-white lg:text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={fraz.eviscerato}
                                    onChange={(e) => {
                                      const newFrazioni = [...frazionamento.frazioni];
                                      newFrazioni[idx].eviscerato = e.target.checked;
                                      setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                    }}
                                    className="w-4 h-4 rounded"
                                  />
                                  Eviscerato
                                </label>
                                <label className="flex items-center gap-2 text-xs text-white lg:text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={fraz.presenza_vermi}
                                    onChange={(e) => {
                                      const newFrazioni = [...frazionamento.frazioni];
                                      newFrazioni[idx].presenza_vermi = e.target.checked;
                                      setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                    }}
                                    className="w-4 h-4 rounded"
                                  />
                                  Presenza vermi
                                </label>
                              </div>
                            )}
                            
                            {/* Date Inizio/Fine per metodi che lo richiedono */}
                            {['abbattimento', 'congelamento', 'marinatura', 'affumicatura', 'pastorizzazione'].includes(fraz.conservazione) && (
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-[10px] text-blue-300 lg:text-blue-600">Inizio</label>
                                  <div className="flex gap-1">
                                    <input
                                      type="date"
                                      value={fraz.inizio_data}
                                      onChange={(e) => {
                                        const newFrazioni = [...frazionamento.frazioni];
                                        newFrazioni[idx].inizio_data = e.target.value;
                                        setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                      }}
                                      className="flex-1 px-1 py-1 rounded text-[10px] bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-blue-500/30"
                                    />
                                    <input
                                      type="time"
                                      value={fraz.inizio_ora}
                                      onChange={(e) => {
                                        const newFrazioni = [...frazionamento.frazioni];
                                        newFrazioni[idx].inizio_ora = e.target.value;
                                        setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                      }}
                                      className="w-16 px-1 py-1 rounded text-[10px] bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-blue-500/30"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-[10px] text-blue-300 lg:text-blue-600">Fine</label>
                                  <div className="flex gap-1">
                                    <input
                                      type="date"
                                      value={fraz.fine_data}
                                      onChange={(e) => {
                                        const newFrazioni = [...frazionamento.frazioni];
                                        newFrazioni[idx].fine_data = e.target.value;
                                        setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                      }}
                                      className="flex-1 px-1 py-1 rounded text-[10px] bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-blue-500/30"
                                    />
                                    <input
                                      type="time"
                                      value={fraz.fine_ora}
                                      onChange={(e) => {
                                        const newFrazioni = [...frazionamento.frazioni];
                                        newFrazioni[idx].fine_ora = e.target.value;
                                        setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                      }}
                                      className="w-16 px-1 py-1 rounded text-[10px] bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-blue-500/30"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Temperatura per metodi che la richiedono */}
                            {['abbattimento', 'congelamento', 'sottovuoto', 'marinatura', 'affumicatura', 'pastorizzazione'].includes(fraz.conservazione) && (
                              <div className="flex items-center gap-2">
                                <label className="text-[10px] text-blue-300 lg:text-blue-600">Temperatura:</label>
                                <input
                                  type="number"
                                  value={fraz.temperatura}
                                  onChange={(e) => {
                                    const newFrazioni = [...frazionamento.frazioni];
                                    newFrazioni[idx].temperatura = parseFloat(e.target.value);
                                    setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                  }}
                                  className="w-16 px-2 py-1 rounded text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-blue-500/30 text-center"
                                />
                                <span className="text-[10px] text-blue-300 lg:text-blue-600">°C</span>
                              </div>
                            )}
                            
                            {/* Note */}
                            <div>
                              <input
                                type="text"
                                value={fraz.note_conservazione}
                                onChange={(e) => {
                                  const newFrazioni = [...frazionamento.frazioni];
                                  newFrazioni[idx].note_conservazione = e.target.value;
                                  setFrazionamento({...frazionamento, frazioni: newFrazioni});
                                }}
                                placeholder="Note (opzionale)"
                                className="w-full px-2 py-1 rounded text-[10px] bg-slate-700 lg:bg-white text-white lg:text-gray-900 border border-blue-500/30"
                              />
                            </div>
                            
                            {/* Scadenza calcolata */}
                            <p className="text-[10px] text-center text-blue-400 lg:text-blue-700">
                              📅 Scadenza: {(() => {
                                const dataProd = new Date(lottoForm.data_produzione);
                                const dataScad = new Date(dataProd);
                                dataScad.setDate(dataScad.getDate() + fraz.giorni_scadenza);
                                return `${String(dataScad.getDate()).padStart(2, '0')}/${String(dataScad.getMonth() + 1).padStart(2, '0')}/${dataScad.getFullYear()}`;
                              })()} (+{fraz.giorni_scadenza} giorni)
                            </p>
                          </div>
                        )}
                        
                        {/* Se nessun metodo selezionato */}
                        {!fraz.conservazione && (
                          <p className="text-[10px] text-yellow-400 lg:text-yellow-700 bg-yellow-500/10 lg:bg-yellow-50 p-2 rounded">
                            ⚠️ Senza conservazione: scadenza 3 giorni dalla produzione
                          </p>
                        )}
                      </div>
                    ))}
                    
                    {/* Riepilogo finale */}
                    <div className="bg-slate-800/60 lg:bg-gray-100 rounded-lg p-2 text-xs">
                      <p className="text-center text-slate-300 lg:text-gray-600 font-medium mb-1">
                        Riepilogo: verranno creati {frazionamento.frazioni.length} lotti separati
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {frazionamento.frazioni.map((f, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-500/20 lg:bg-purple-100 text-purple-300 lg:text-purple-700 rounded text-[10px]">
                            {f.quantita || 0} {lottoForm.unita_misura} → {f.conservazione ? METODI_CONSERVAZIONE.find(m => m.id === f.conservazione)?.label : 'Fresco'}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Toggle conservazione (se non frazionato) */}
                {!frazionamento.abilitato && (
                  <>
                    <div className="flex items-center justify-between p-4 bg-black/40 backdrop-blur-md lg:bg-gray-50 rounded-xl border border-slate-700 lg:border-transparent">
                      <div>
                        <p className="font-medium text-white lg:text-gray-900 text-sm">Metodo di conservazione?</p>
                        <p className="text-xs text-slate-400 lg:text-gray-500">Senza conservazione: scade in 3 giorni</p>
                      </div>
                      <button
                        onClick={() => setConservazione({...conservazione, necessaria: !conservazione.necessaria})}
                        className={`w-12 h-6 rounded-full transition-colors ${conservazione.necessaria ? 'bg-blue-500' : 'bg-slate-600 lg:bg-gray-300'}`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${conservazione.necessaria ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </button>
                    </div>

                    {/* Form conservazione */}
                    {conservazione.necessaria && (
                      <div className="bg-blue-500/10 lg:bg-blue-50 rounded-xl p-3 space-y-3 border border-blue-500/30 lg:border-blue-200">
                        <h4 className="font-semibold text-blue-400 lg:text-blue-900 text-sm flex items-center justify-center gap-2">
                          📋 Scheda di Conservazione
                        </h4>
                        
                        {/* Selezione metodo di conservazione */}
                        <div>
                          <label className="text-xs text-blue-300 lg:text-blue-700 mb-2 block">Metodo di Conservazione</label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                            {METODI_CONSERVAZIONE.map(metodo => (
                              <button
                                key={metodo.id}
                                onClick={() => setConservazione({
                                  ...conservazione, 
                                  metodo: metodo.id,
                                  temperatura: metodo.temp || conservazione.temperatura
                                })}
                                className={`p-2 rounded-lg text-center transition-all ${
                                  conservazione.metodo === metodo.id
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-slate-700/50 lg:bg-white text-white lg:text-gray-700 hover:bg-slate-600 lg:hover:bg-blue-50'
                                }`}
                              >
                                <span className="text-lg">{metodo.icon}</span>
                                <p className="text-[10px] mt-1">{metodo.label}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Toggle Eviscerato e Presenza vermi (solo per abbattimento/congelamento) */}
                        {['abbattimento', 'congelamento'].includes(conservazione.metodo) && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-3 bg-slate-800/40 backdrop-blur-md lg:bg-white/90 backdrop-blur-sm rounded-lg">
                              <span className="text-sm text-white lg:text-gray-900">Eviscerato</span>
                              <button
                                onClick={() => setConservazione({...conservazione, eviscerato: !conservazione.eviscerato})}
                                className={`w-11 h-6 rounded-full transition-colors ${conservazione.eviscerato ? 'bg-green-500' : 'bg-slate-600 lg:bg-gray-300'}`}
                              >
                                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${conservazione.eviscerato ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-slate-800/40 backdrop-blur-md lg:bg-white/90 backdrop-blur-sm rounded-lg">
                              <span className="text-sm text-white lg:text-gray-900">Presenza di vermi</span>
                              <button
                                onClick={() => setConservazione({...conservazione, presenza_vermi: !conservazione.presenza_vermi})}
                                className={`w-11 h-6 rounded-full transition-colors ${conservazione.presenza_vermi ? 'bg-red-500' : 'bg-slate-600 lg:bg-gray-300'}`}
                              >
                                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${conservazione.presenza_vermi ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Date Inizio/Fine (per metodi che lo richiedono) */}
                        {['abbattimento', 'congelamento', 'marinatura', 'affumicatura', 'pastorizzazione'].includes(conservazione.metodo) && (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col items-center space-y-2">
                              <p className="text-xs text-blue-300 lg:text-blue-600 font-medium">Inizio</p>
                              <input
                                type="date"
                                value={conservazione.inizio_data}
                                onChange={(e) => setConservazione({...conservazione, inizio_data: e.target.value})}
                                className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                              />
                              <input
                                type="time"
                                value={conservazione.inizio_ora}
                                onChange={(e) => setConservazione({...conservazione, inizio_ora: e.target.value})}
                                className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                              />
                            </div>
                            
                            <div className="flex flex-col items-center space-y-2">
                              <p className="text-xs text-blue-300 lg:text-blue-600 font-medium">Fine</p>
                              <input
                                type="date"
                                value={conservazione.fine_data}
                                onChange={(e) => setConservazione({...conservazione, fine_data: e.target.value})}
                                className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                              />
                              <input
                                type="time"
                                value={conservazione.fine_ora}
                                onChange={(e) => setConservazione({...conservazione, fine_ora: e.target.value})}
                                className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Temperatura (per metodi che la richiedono) */}
                        {['abbattimento', 'congelamento', 'sottovuoto', 'marinatura', 'affumicatura', 'pastorizzazione'].includes(conservazione.metodo) && (
                          <div className="flex flex-col items-center">
                            <label className="block text-xs text-blue-300 lg:text-blue-700 mb-1">Temperatura (°C)</label>
                            <input
                              type="number"
                              value={conservazione.temperatura}
                              onChange={(e) => setConservazione({...conservazione, temperatura: parseFloat(e.target.value)})}
                              className="w-24 px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-center text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                            />
                          </div>
                        )}

                        {/* Note conservazione */}
                        <div>
                          <label className="block text-xs text-blue-300 lg:text-blue-700 mb-1">Note (opzionale)</label>
                          <textarea
                            value={conservazione.note_conservazione}
                            onChange={(e) => setConservazione({...conservazione, note_conservazione: e.target.value})}
                            placeholder="Es: Conservare in luogo fresco e asciutto..."
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
                {/* Allergeni rilevati - visibili e modificabili prima del salvataggio */}
                <AllergeniEditor selezionati={allergeniSelezionati} auto={allergeniAuto} onToggle={toggleAllergene} />
              </div>
            )}

            {/* Footer con pulsanti - In basso del modal */}
            <div className="p-3 border-t border-slate-700/50 lg:border-gray-100 flex gap-2 flex-shrink-0 bg-black/90 lg:bg-white" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}>
              <button
                onClick={closeImportModal}
                className="py-3 lg:py-2.5 px-4 bg-slate-700 lg:bg-gray-100 hover:bg-slate-600 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium text-sm transition-colors"
              >
                Annulla
              </button>
              {importStep === 2 && (
                <button
                  onClick={() => setImportStep(3)}
                  className="flex-1 py-3 lg:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors"
                >
                  Continua
                </button>
              )}
              {importStep === 3 && (
                <>
                  <button
                    onClick={() => salvaLottoProduzione(false)}
                    disabled={saving}
                    className="flex-1 py-3 lg:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-1"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Salva
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => salvaLottoProduzione(true)}
                    disabled={saving}
                    className="flex-1 py-3 lg:py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-1"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Printer className="h-4 w-4" />
                        Stampa
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NUOVO Modal: Crea Ricetta Manualmente - FULLSCREEN su mobile */}
      {showCreaRicettaModal && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[100]">
          {/* Mobile Background Image */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-lg lg:max-h-[90vh] overflow-hidden flex flex-col relative z-10">
            {/* Header */}
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-purple-50 lg:to-blue-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => {
                    if (creaRicettaStep > 1) {
                      setCreaRicettaStep(creaRicettaStep - 1);
                    } else {
                      setShowCreaRicettaModal(false);
                    }
                  }} 
                  className="p-2 bg-black/40 backdrop-blur-md lg:bg-white/50 hover:bg-slate-700 lg:hover:bg-white/90 backdrop-blur-sm rounded-xl"
                >
                  <ChevronLeft className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Crea Ricetta</h3>
                  <p className="text-xs text-purple-400 lg:text-purple-600">
                    Step {creaRicettaStep}/2 • {creaRicettaStep === 1 ? 'Ricetta' : 'Conservazione'}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowCreaRicettaModal(false)} className="p-1.5 bg-slate-700 lg:bg-white/50 hover:bg-slate-600 rounded-lg">
                <X className="h-4 w-4 text-white lg:text-gray-500" />
              </button>
            </div>

            {/* Step indicators - 2 step */}
            <div className="flex items-center gap-2 p-3 bg-slate-800/40 backdrop-blur-md lg:bg-gray-50 border-b border-slate-700/50 lg:border-gray-100">
              {[1, 2].map((s) => (
                <div key={s} className={`flex-1 h-1.5 rounded-full ${s <= creaRicettaStep ? 'bg-purple-500' : 'bg-slate-700 lg:bg-gray-200'}`} />
              ))}
            </div>

            {/* Step 1: Nome + Data/Qta + Ingredienti */}
            {creaRicettaStep === 1 && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-40 lg:pb-4">
                {/* Nome piatto + Numero Lotto inline */}
                <div className="flex gap-3 items-start">
                  <div className="flex-1 min-w-0">
                    <label className="text-sm font-medium text-white lg:text-gray-700">Nome del Piatto *</label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="text"
                        value={nuovaRicetta.nome}
                        onChange={(e) => setNuovaRicetta({...nuovaRicetta, nome: e.target.value})}
                        placeholder="Es: Lasagna al Ragù"
                        data-testid="crea-ricetta-nome-input"
                        className="flex-1 min-w-0 px-3 py-2.5 bg-black/40 backdrop-blur-md lg:bg-white border border-slate-700/50 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <button
                        onClick={cercaRicettaConAI}
                        disabled={cercandoRicettaAI || !nuovaRicetta.nome || nuovaRicetta.nome.length < 3}
                        data-testid="cerca-ricetta-ai-btn"
                        className={`px-3 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                          cercandoRicettaAI 
                            ? 'bg-purple-500/50 text-white cursor-wait' 
                            : nuovaRicetta.nome && nuovaRicetta.nome.length >= 3
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'bg-slate-700 lg:bg-gray-200 text-slate-400 lg:text-gray-400 cursor-not-allowed'
                        }`}
                        title="Trova ingredienti con AI"
                      >
                        {cercandoRicettaAI ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="hidden sm:inline">Cerco...</span>
                          </>
                        ) : (
                          <>
                            <span>🤖</span>
                            <span className="hidden sm:inline">Trova Ricetta</span>
                          </>
                        )}
                      </button>
                    </div>
                    {nuovaRicetta.nome && nuovaRicetta.nome.length >= 3 && (
                      <p className="text-[10px] text-purple-400 lg:text-purple-600 mt-1">
                        💡 Clicca "Trova Ricetta" per ottenere gli ingredienti automaticamente
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <label className="text-sm font-medium text-purple-400 lg:text-purple-600">Lotto</label>
                    <div className="mt-1 px-2.5 py-2.5 bg-purple-600/20 lg:bg-purple-50 border border-purple-500/30 lg:border-purple-200 rounded-xl text-center">
                      <p className="font-bold text-white lg:text-purple-700 text-sm whitespace-nowrap" data-testid="numero-lotto">{numeroLotto}</p>
                    </div>
                  </div>
                </div>

                {/* Data, Quantità, Unità - responsive layout */}
                <div className="space-y-3">
                  {/* Data produzione - full width */}
                  <div className="w-full">
                    <label className="block text-xs font-medium text-white lg:text-gray-700 mb-1">Data Produzione</label>
                    <input
                      type="date"
                      value={lottoForm.data_produzione}
                      onChange={(e) => setLottoForm({...lottoForm, data_produzione: e.target.value})}
                      className="w-full px-3 py-2.5 bg-black/40 backdrop-blur-md lg:bg-white border border-slate-700/50 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 text-sm text-left"
                      style={{ WebkitAppearance: 'none', textAlign: 'left' }}
                    />
                  </div>
                  {/* Quantità e Unità su stessa riga */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-white lg:text-gray-700 mb-1">Quantità</label>
                      <input
                        type="number"
                        value={lottoForm.quantita}
                        onChange={(e) => setLottoForm({...lottoForm, quantita: e.target.value})}
                        placeholder="1"
                        className="w-full px-3 py-2.5 bg-black/40 backdrop-blur-md lg:bg-white border border-slate-700/50 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-white lg:text-gray-700 mb-1">Unità</label>
                      <select
                        value={lottoForm.unita_misura}
                        onChange={(e) => setLottoForm({...lottoForm, unita_misura: e.target.value})}
                        className="w-full px-3 py-2.5 bg-black/40 backdrop-blur-md lg:bg-white border border-slate-700/50 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 text-sm"
                      >
                        <option value="porzioni">porzioni</option>
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                        <option value="pz">pz</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Scadenza Lotto */}
                <div>
                  <label className="block text-xs font-medium text-white lg:text-gray-700 mb-1">Scadenza Lotto</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {['30', '60', '90', '120'].map((giorni) => (
                      <button
                        key={giorni}
                        type="button"
                        data-testid={`crea-scadenza-${giorni}gg`}
                        onClick={() => setLottoForm({ ...lottoForm, tipo_scadenza: lottoForm.tipo_scadenza === giorni ? '' : giorni, scadenza_custom: '' })}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          lottoForm.tipo_scadenza === giorni
                            ? 'bg-cyan-600 text-white'
                            : 'bg-slate-700 lg:bg-gray-200 text-slate-300 lg:text-gray-700 hover:bg-slate-600 lg:hover:bg-gray-300'
                        }`}
                      >
                        {giorni}gg
                      </button>
                    ))}
                    <button
                      type="button"
                      data-testid="crea-scadenza-custom"
                      onClick={() => setLottoForm({ ...lottoForm, tipo_scadenza: lottoForm.tipo_scadenza === 'custom' ? '' : 'custom' })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        lottoForm.tipo_scadenza === 'custom'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-slate-700 lg:bg-gray-200 text-slate-300 lg:text-gray-700 hover:bg-slate-600 lg:hover:bg-gray-300'
                      }`}
                    >
                      Data precisa
                    </button>
                  </div>
                  {lottoForm.tipo_scadenza === 'custom' && (
                    <input
                      type="date"
                      data-testid="crea-scadenza-data"
                      value={lottoForm.scadenza_custom}
                      onChange={(e) => setLottoForm({ ...lottoForm, scadenza_custom: e.target.value })}
                      className="w-full px-3 py-2.5 bg-black/40 backdrop-blur-md lg:bg-white border border-slate-700/50 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 text-sm"
                    />
                  )}
                  {lottoForm.tipo_scadenza && lottoForm.tipo_scadenza !== 'custom' && (
                    <p className="text-xs text-slate-400 lg:text-gray-400">
                      Scadenza: {(() => {
                        const dataScad = new Date(lottoForm.data_produzione);
                        dataScad.setDate(dataScad.getDate() + parseInt(lottoForm.tipo_scadenza));
                        return `${String(dataScad.getDate()).padStart(2, '0')}/${String(dataScad.getMonth() + 1).padStart(2, '0')}/${dataScad.getFullYear()}`;
                      })()}
                    </p>
                  )}
                  {!lottoForm.tipo_scadenza && (
                    <p className="text-xs text-yellow-500 lg:text-yellow-600">⚠️ Senza selezione: scadenza dal metodo di conservazione (o 3 giorni)</p>
                  )}
                </div>

                {/* Separatore */}
                <div className="border-t border-slate-700/50 lg:border-gray-200 pt-4">
                  <label className="text-sm font-medium text-white lg:text-gray-700 flex items-center gap-2">
                    Ingredienti ({nuovaRicetta.ingredienti.length})
                    <span className="text-xs text-slate-400 lg:text-gray-500 font-normal">Riconcilia con DDT</span>
                  </label>
                </div>

                {/* Lista Ingredienti aggiunti */}
                {nuovaRicetta.ingredienti.length > 0 && (
                  <div className="space-y-2">
                    {nuovaRicetta.ingredienti.map((ing, idx) => (
                      <div key={ing.id} className={`rounded-xl border overflow-hidden ${
                        ing.riconciliato 
                          ? 'bg-green-600/10 border-green-500/30 lg:bg-green-50 lg:border-green-200' 
                          : 'bg-slate-800 border-slate-700 lg:bg-white lg:border-gray-200'
                      }`}>
                        {/* Riga principale ingrediente */}
                        <div className="flex items-center gap-2 p-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white lg:text-gray-900 text-sm truncate">{ing.nome}</p>
                            <p className="text-xs text-slate-400 lg:text-gray-500">
                              {ing.quantita} {ing.unita_misura}
                            </p>
                          </div>
                          {ing.riconciliato && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 lg:bg-green-100 lg:text-green-700 text-xs rounded-full flex-shrink-0">DDT ✓</span>
                          )}
                          {/* Pulsante modifica/abbina */}
                          <button 
                            onClick={() => editingIngredienteIdx === idx ? setEditingIngredienteIdx(null) : apriModificaIngrediente(idx)}
                            className={`p-1.5 rounded-lg flex-shrink-0 ${
                              editingIngredienteIdx === idx 
                                ? 'bg-blue-500 text-white' 
                                : 'hover:bg-blue-500/20 text-blue-400'
                            }`}
                            title={ing.riconciliato ? "Modifica abbinamento" : "Abbina a DDT"}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => rimuoviIngredienteCrea(idx)} className="p-1.5 hover:bg-red-500/20 rounded-lg flex-shrink-0">
                            <X className="h-4 w-4 text-red-400" />
                          </button>
                        </div>
                        
                        {/* Dettagli prodotto DDT abbinato */}
                        {ing.riconciliato && ing.prodotto_ddt && editingIngredienteIdx !== idx && (
                          <div className="px-3 pb-3 pt-0">
                            <div className="p-2 bg-green-500/10 lg:bg-green-100/50 rounded-lg text-xs">
                              <p className="text-green-300 lg:text-green-800 font-medium truncate">
                                📦 {ing.prodotto_ddt.nome_prodotto}
                              </p>
                              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-green-400/80 lg:text-green-700/80">
                                {ing.prodotto_ddt.fornitore && (
                                  <span>🏪 {ing.prodotto_ddt.fornitore}</span>
                                )}
                                {ing.prodotto_ddt.numero_lotto && (
                                  <span>🏷️ Lotto: {ing.prodotto_ddt.numero_lotto}</span>
                                )}
                                {ing.prodotto_ddt.scadenza && (
                                  <span>📅 Scad: {ing.prodotto_ddt.scadenza}</span>
                                )}
                                {ing.prodotto_ddt.ddt_numero && (
                                  <span>📄 DDT: {ing.prodotto_ddt.ddt_numero}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Pannello modifica abbinamento */}
                        {editingIngredienteIdx === idx && (
                          <div className="p-3 border-t border-slate-700/50 lg:border-gray-200 bg-slate-900/50 lg:bg-gray-100">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-white lg:text-gray-700">Seleziona prodotto DDT:</p>
                              {ing.riconciliato && (
                                <button 
                                  onClick={() => rimuoviAbbinamentoDdt(idx)}
                                  className="text-xs text-red-400 hover:underline"
                                >
                                  Rimuovi abbinamento
                                </button>
                              )}
                            </div>
                            
                            {/* Ricerca manuale */}
                            <div className="flex gap-2 mb-2">
                              <input
                                type="text"
                                placeholder="Cerca prodotto..."
                                defaultValue={ing.nome}
                                onChange={(e) => {
                                  if (e.target.value.length >= 2) {
                                    cercaDdtPerModifica(e.target.value);
                                  }
                                }}
                                className="flex-1 px-2 py-1.5 text-xs rounded-lg bg-slate-800 lg:bg-white border border-slate-600 lg:border-gray-300 text-white lg:text-gray-900"
                              />
                            </div>
                            
                            {/* Risultati ricerca */}
                            {searchingDdtForEdit ? (
                              <p className="text-xs text-slate-400 text-center py-2">Ricerca in corso...</p>
                            ) : ddtSearchForEdit.length > 0 ? (
                              <div className="max-h-56 overflow-y-auto">
                                <GruppiDdtRisultati
                                  gruppi={ddtSearchForEditGruppi}
                                  risultati={ddtSearchForEdit}
                                  onSelect={(prod) => selezionaNuovoDdt(idx, prod)}
                                  testIdPrefix="edit-gruppo-ddt"
                                />
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400 lg:text-gray-500 text-center py-2">
                                Nessun prodotto trovato nei DDT
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Form Aggiungi Ingrediente */}
                <div className="p-4 bg-slate-800/40 backdrop-blur-md lg:bg-gray-50 rounded-xl border border-slate-700/50 lg:border-gray-200">
                  <p className="text-sm font-medium text-white lg:text-gray-700 mb-3">Aggiungi Ingrediente</p>
                  
                  {/* Ricerca DDT */}
                  <div className="relative mb-3">
                    <input
                      type="text"
                      value={nuovoIngredienteCrea.nome}
                      onChange={(e) => {
                        setNuovoIngredienteCrea({...nuovoIngredienteCrea, nome: e.target.value, prodotto_ddt: null});
                        if (e.target.value.length >= 2) {
                          cercaProdottiDdt(e.target.value);
                        } else {
                          setDdtSearchResults([]);
                        }
                      }}
                      placeholder="Cerca prodotto nei DDT..."
                      data-testid="cerca-ingrediente-ddt-input"
                      className="w-full px-3 py-2.5 bg-black/60 backdrop-blur-md lg:bg-white border border-slate-600 lg:border-gray-300 rounded-xl text-white lg:text-gray-900 focus:ring-2 focus:ring-purple-500 pl-10"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    {searchingDdt && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent" />
                      </div>
                    )}
                  </div>

                  {/* Risultati ricerca DDT */}
                  {ddtSearchResults.length > 0 && (
                    <div className="mb-3 max-h-64 overflow-y-auto bg-slate-900/60 backdrop-blur-sm lg:bg-white border border-purple-500/30 lg:border-purple-200 rounded-xl" data-testid="ddt-search-results">
                      <div className="p-2 bg-purple-600/20 lg:bg-purple-50 border-b border-purple-500/30 lg:border-purple-200">
                        <p className="text-xs text-purple-400 lg:text-purple-700">{ddtSearchResultsGruppi.length || ddtSearchResults.length} prodotti nei DDT (ultimi 5 arrivi per prodotto)</p>
                      </div>
                      <div className="p-2">
                        <GruppiDdtRisultati
                          gruppi={ddtSearchResultsGruppi}
                          risultati={ddtSearchResults}
                          onSelect={selezionaProdottoDdt}
                          testIdPrefix="crea-gruppo-ddt"
                        />
                      </div>
                    </div>
                  )}

                  {/* Campi aggiuntivi */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <input
                      type="number"
                      value={nuovoIngredienteCrea.quantita}
                      onChange={(e) => setNuovoIngredienteCrea({...nuovoIngredienteCrea, quantita: e.target.value})}
                      placeholder="Qtà"
                      className="px-3 py-2 bg-black/60 backdrop-blur-md lg:bg-white border border-slate-600 lg:border-gray-300 rounded-xl text-white lg:text-gray-900 text-sm"
                    />
                    <select
                      value={nuovoIngredienteCrea.unita_misura}
                      onChange={(e) => setNuovoIngredienteCrea({...nuovoIngredienteCrea, unita_misura: e.target.value})}
                      className="px-2 py-2 bg-black/60 backdrop-blur-md lg:bg-white border border-slate-600 lg:border-gray-300 rounded-xl text-white lg:text-gray-900 text-sm"
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                      <option value="l">l</option>
                      <option value="pz">pz</option>
                    </select>
                    <input
                      type="text"
                      value={nuovoIngredienteCrea.lotto}
                      onChange={(e) => setNuovoIngredienteCrea({...nuovoIngredienteCrea, lotto: e.target.value})}
                      placeholder="Lotto"
                      className="px-3 py-2 bg-black/60 backdrop-blur-md lg:bg-white border border-slate-600 lg:border-gray-300 rounded-xl text-white lg:text-gray-900 text-sm"
                    />
                  </div>

                  <button
                    onClick={aggiungiIngredienteCrea}
                    data-testid="aggiungi-ingrediente-btn"
                    className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Aggiungi Ingrediente
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Conservazione */}
            {creaRicettaStep === 2 && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-40 lg:pb-4">
                {/* Riepilogo ricetta */}
                <div className="bg-purple-600/20 lg:bg-purple-50 rounded-xl p-3 flex items-center gap-3 border border-purple-500/30 lg:border-transparent">
                  <div className="w-10 h-10 rounded-lg bg-purple-600/30 lg:bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-purple-400 lg:text-purple-700">{numeroLotto?.split('/')[0]}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-white lg:text-gray-900 text-sm truncate">{nuovaRicetta.nome}</p>
                    <p className="text-xs text-purple-400 lg:text-purple-600">{nuovaRicetta.ingredienti.length} ingredienti • {lottoForm.quantita} {lottoForm.unita_misura}</p>
                  </div>
                </div>

                {/* Toggle conservazione */}
                <div className="flex items-center justify-between p-4 bg-black/40 backdrop-blur-md lg:bg-gray-50 rounded-xl border border-slate-700 lg:border-transparent">
                  <div>
                    <p className="font-medium text-white lg:text-gray-900 text-sm">Metodo di conservazione?</p>
                    <p className="text-xs text-slate-400 lg:text-gray-500">Senza conservazione: scade in 3 giorni</p>
                  </div>
                  <button
                    onClick={() => setConservazione({...conservazione, necessaria: !conservazione.necessaria})}
                    className={`w-12 h-6 rounded-full transition-colors ${conservazione.necessaria ? 'bg-blue-500' : 'bg-slate-600 lg:bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${conservazione.necessaria ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {conservazione.necessaria && (
                  <div className="bg-blue-500/10 lg:bg-blue-50 rounded-xl p-3 space-y-3 border border-blue-500/30 lg:border-blue-200">
                    <h4 className="font-semibold text-blue-400 lg:text-blue-900 text-sm flex items-center justify-center gap-2">
                      📋 Scheda di Conservazione
                    </h4>
                    
                    {/* Selezione metodo di conservazione */}
                    <div>
                      <label className="text-xs text-blue-300 lg:text-blue-700 mb-2 block">Metodo di Conservazione</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {METODI_CONSERVAZIONE.map(metodo => (
                          <button
                            key={metodo.id}
                            onClick={() => setConservazione({
                              ...conservazione, 
                              metodo: metodo.id,
                              temperatura: metodo.temp || conservazione.temperatura
                            })}
                            className={`p-2 rounded-lg text-center transition-all ${
                              conservazione.metodo === metodo.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-slate-700/50 lg:bg-white text-white lg:text-gray-700 hover:bg-slate-600 lg:hover:bg-blue-50'
                            }`}
                          >
                            <span className="text-lg">{metodo.icon}</span>
                            <p className="text-[10px] mt-1">{metodo.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Toggle Eviscerato e Presenza vermi (solo per abbattimento/congelamento) */}
                    {['abbattimento', 'congelamento'].includes(conservazione.metodo) && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 bg-slate-800/40 backdrop-blur-md lg:bg-white/90 backdrop-blur-sm rounded-lg">
                          <span className="text-sm text-white lg:text-gray-900">Eviscerato</span>
                          <button
                            onClick={() => setConservazione({...conservazione, eviscerato: !conservazione.eviscerato})}
                            className={`w-11 h-6 rounded-full transition-colors ${conservazione.eviscerato ? 'bg-green-500' : 'bg-slate-600 lg:bg-gray-300'}`}
                          >
                            <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${conservazione.eviscerato ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-slate-800/40 backdrop-blur-md lg:bg-white/90 backdrop-blur-sm rounded-lg">
                          <span className="text-sm text-white lg:text-gray-900">Presenza di vermi</span>
                          <button
                            onClick={() => setConservazione({...conservazione, presenza_vermi: !conservazione.presenza_vermi})}
                            className={`w-11 h-6 rounded-full transition-colors ${conservazione.presenza_vermi ? 'bg-red-500' : 'bg-slate-600 lg:bg-gray-300'}`}
                          >
                            <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${conservazione.presenza_vermi ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Date Inizio/Fine (per metodi che lo richiedono) */}
                    {['abbattimento', 'congelamento', 'marinatura', 'affumicatura', 'pastorizzazione'].includes(conservazione.metodo) && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col items-center space-y-2">
                          <p className="text-xs text-blue-300 lg:text-blue-600 font-medium">Inizio</p>
                          <input
                            type="date"
                            value={conservazione.inizio_data}
                            onChange={(e) => setConservazione({...conservazione, inizio_data: e.target.value})}
                            className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                          />
                          <input
                            type="time"
                            value={conservazione.inizio_ora}
                            onChange={(e) => setConservazione({...conservazione, inizio_ora: e.target.value})}
                            className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                          />
                        </div>
                        
                        <div className="flex flex-col items-center space-y-2">
                          <p className="text-xs text-blue-300 lg:text-blue-600 font-medium">Fine</p>
                          <input
                            type="date"
                            value={conservazione.fine_data}
                            onChange={(e) => setConservazione({...conservazione, fine_data: e.target.value})}
                            className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                          />
                          <input
                            type="time"
                            value={conservazione.fine_ora}
                            onChange={(e) => setConservazione({...conservazione, fine_ora: e.target.value})}
                            className="w-full px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Temperatura (per metodi che la richiedono) */}
                    {['abbattimento', 'congelamento', 'sottovuoto', 'marinatura', 'affumicatura', 'pastorizzazione'].includes(conservazione.metodo) && (
                      <div className="flex flex-col items-center">
                        <label className="block text-xs text-blue-300 lg:text-blue-700 mb-1">Temperatura (°C)</label>
                        <input
                          type="number"
                          value={conservazione.temperatura}
                          onChange={(e) => setConservazione({...conservazione, temperatura: parseFloat(e.target.value)})}
                          className="w-24 px-2 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-center text-sm bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                        />
                      </div>
                    )}

                    {/* Note conservazione */}
                    <div>
                      <label className="block text-xs text-blue-300 lg:text-blue-700 mb-1">Note (opzionale)</label>
                      <textarea
                        value={conservazione.note_conservazione}
                        onChange={(e) => setConservazione({...conservazione, note_conservazione: e.target.value})}
                        placeholder="Es: Conservare in luogo fresco e asciutto..."
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg border border-blue-500/30 lg:border-blue-200 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900"
                      />
                    </div>
                  </div>
                )}

                {!conservazione.necessaria && (
                  <div className="bg-yellow-600/20 lg:bg-yellow-50 rounded-xl p-3 border border-yellow-500/30 lg:border-yellow-200">
                    <p className="text-xs text-yellow-400 lg:text-yellow-800">
                      ⚠️ Senza metodo di conservazione, la scadenza sarà a <strong>3 giorni</strong> dalla produzione
                    </p>
                  </div>
                )}

                {/* Allergeni rilevati - visibili e modificabili prima del salvataggio */}
                <AllergeniEditor selezionati={allergeniSelezionati} auto={allergeniAuto} onToggle={toggleAllergene} />
              </div>
            )}

            {/* Footer */}
            <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-3 border-t border-slate-700/50 lg:border-gray-100 flex gap-2 flex-shrink-0 bg-black/60 backdrop-blur-md lg:bg-white z-[101]">
              <button
                onClick={() => {
                  if (creaRicettaStep === 1) {
                    setShowCreaRicettaModal(false);
                  } else {
                    setCreaRicettaStep(creaRicettaStep - 1);
                  }
                }}
                className="py-3 lg:py-2.5 px-4 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium text-sm"
              >
                {creaRicettaStep === 1 ? 'Annulla' : 'Indietro'}
              </button>
              
              {creaRicettaStep < 2 ? (
                <button
                  onClick={() => {
                    if (creaRicettaStep === 1 && (!nuovaRicetta.nome || nuovaRicetta.ingredienti.length === 0)) {
                      toast.error('Inserisci nome e almeno un ingrediente');
                      return;
                    }
                    setCreaRicettaStep(creaRicettaStep + 1);
                  }}
                  data-testid="crea-ricetta-continua-btn"
                  className="flex-1 py-3 lg:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium text-sm"
                >
                  Continua
                </button>
              ) : (
                <>
                  <button
                    onClick={salvaLottoCreaRicetta}
                    disabled={saving}
                    data-testid="crea-ricetta-salva-btn"
                    className="flex-1 py-3 lg:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-1"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Salva & Stampa
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Riconciliazione ingrediente - FULLSCREEN su mobile */}
      {showRiconciliaModal && ingredienteSelezionato && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[110]">
          {/* Mobile Background Image */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-md lg:max-h-[80vh] overflow-hidden flex flex-col relative z-10">
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-green-50 lg:to-emerald-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowRiconciliaModal(false)} className="p-2 bg-slate-800 lg:hidden hover:bg-slate-700 rounded-xl">
                  <ChevronLeft className="h-5 w-5 text-white" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Riconcilia Ingrediente</h3>
                  <p className="text-xs text-green-400 lg:text-green-600">{ingredienteSelezionato.nome_ingrediente}</p>
                </div>
              </div>
              <button onClick={() => setShowRiconciliaModal(false)} className="p-1.5 bg-slate-700 lg:bg-white/50 hover:bg-slate-600 rounded-lg hidden lg:block">
                <X className="h-4 w-4 text-white lg:text-gray-500" />
              </button>
            </div>
            
            {/* TAB: DDT / Prodotti Interni */}
            <div className="flex border-b border-white/10 lg:border-gray-200">
              <button 
                onClick={() => setRiconciliaTab('ddt')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  riconciliaTab === 'ddt' 
                    ? 'text-green-400 lg:text-green-600 border-b-2 border-green-500' 
                    : 'text-slate-400 lg:text-gray-500 hover:text-white lg:hover:text-gray-700'
                }`}
              >
                Da Fornitore (DDT)
              </button>
              <button 
                onClick={() => setRiconciliaTab('interni')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  riconciliaTab === 'interni' 
                    ? 'text-blue-400 lg:text-blue-600 border-b-2 border-blue-500' 
                    : 'text-slate-400 lg:text-gray-500 hover:text-white lg:hover:text-gray-700'
                }`}
              >
                Produzione Interna
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 pb-32 lg:pb-4">
              {/* TAB DDT */}
              {riconciliaTab === 'ddt' && (
                <>
                  <p className="text-xs text-slate-400 lg:text-gray-500 mb-3">
                    Seleziona il prodotto corrispondente dai DDT:
                  </p>
                  
                  {loadingDdt ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                  ) : ddtSuggestions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 lg:text-gray-400">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nessun prodotto trovato nei DDT</p>
                      <p className="text-xs mt-1">Prova a cercare tra i prodotti interni</p>
                    </div>
                  ) : (
                    <GruppiDdtRisultati
                      gruppi={ddtSuggestionsGruppi}
                      risultati={ddtSuggestions}
                      onSelect={confermaRiconciliazione}
                      testIdPrefix="riconcilia-gruppo-ddt"
                    />
                  )}
                </>
              )}
              
              {/* TAB PRODOTTI INTERNI */}
              {riconciliaTab === 'interni' && (
                <>
                  <p className="text-xs text-slate-400 lg:text-gray-500 mb-3">
                    Seleziona una preparazione interna (etichetta attiva):
                  </p>
                  
                  {loadingProdottiInterni ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                  ) : prodottiInterni.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 lg:text-gray-400">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nessuna preparazione interna trovata</p>
                      <p className="text-xs mt-1">Crea prima un'etichetta di conservazione</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {prodottiInterni.map((etichetta, idx) => (
                        <button
                          key={idx}
                          onClick={() => confermaRiconciliazioneInterna(etichetta)}
                          className="w-full flex flex-col gap-1 p-3 rounded-lg border border-slate-700/50 lg:border-gray-200 bg-black/40 backdrop-blur-md lg:bg-white hover:border-blue-500 lg:hover:border-blue-300 hover:bg-slate-700 lg:hover:bg-blue-50 transition-colors text-left"
                        >
                          <p className="font-medium text-white lg:text-gray-900 text-sm">{etichetta.nome}</p>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <span className="bg-blue-500/20 lg:bg-blue-100 text-blue-400 lg:text-blue-700 px-2 py-0.5 rounded">
                              Produzione Interna
                            </span>
                            {etichetta.numero_lotto && (
                              <span className="bg-purple-500/20 lg:bg-purple-100 text-purple-400 lg:text-purple-700 px-2 py-0.5 rounded">
                                Lotto: {etichetta.numero_lotto}
                              </span>
                            )}
                            {etichetta.data_consumo && (
                              <span className="bg-orange-500/20 lg:bg-orange-100 text-orange-400 lg:text-orange-700 px-2 py-0.5 rounded">
                                Scad: {etichetta.data_consumo}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 lg:text-gray-400">
                            Prodotto il {etichetta.data_produzione}
                            {etichetta.quantita && ` • ${etichetta.quantita} ${etichetta.unita_misura || 'pz'}`}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Aggiungi Nuovo Ingrediente - FULLSCREEN su mobile */}
      {showAddIngredienteModal && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[120]">
          {/* Mobile Background Image */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-md lg:max-h-[80vh] overflow-hidden flex flex-col relative z-10">
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-green-50 lg:to-emerald-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowAddIngredienteModal(false)} 
                  className="p-2 bg-slate-800 lg:hidden hover:bg-slate-700 rounded-xl"
                >
                  <ChevronLeft className="h-5 w-5 text-white" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Aggiungi Prodotto</h3>
                  <p className="text-xs text-green-400 lg:text-green-600">
                    {addingToDettaglio ? 'Aggiungi al lotto esistente' : 'Aggiungi alla lista ingredienti'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowAddIngredienteModal(false)} 
                className="p-1.5 bg-slate-700 lg:bg-white/50 hover:bg-slate-600 rounded-lg hidden lg:block"
              >
                <X className="h-4 w-4 text-white lg:text-gray-500" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32 lg:pb-4">
              {/* Cerca nei DDT */}
              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-2">
                  Cerca prodotto nei DDT
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Nome prodotto..."
                    data-testid="nuovo-ingrediente-nome-input"
                    value={nuovoIngredienteNome}
                    onChange={(e) => setNuovoIngredienteNome(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && cercaNuovoIngrediente()}
                    className="flex-1 px-3 py-2 rounded-lg border bg-black/40 backdrop-blur-md lg:bg-white border-slate-700/50 lg:border-gray-200 text-sm text-white lg:text-gray-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={cercaNuovoIngrediente}
                    disabled={searchingNewIngrediente}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {searchingNewIngrediente ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Risultati ricerca DDT */}
              {nuovoIngredienteDdtResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-400 lg:text-gray-500 font-medium">Prodotti trovati nei DDT (ultimi 5 arrivi per prodotto):</p>
                  <div className="max-h-64 overflow-y-auto">
                    <GruppiDdtRisultati
                      gruppi={nuovoIngredienteDdtGruppi}
                      risultati={nuovoIngredienteDdtResults}
                      onSelect={aggiungiIngredienteConDdt}
                      testIdPrefix="nuovo-gruppo-ddt"
                    />
                  </div>
                </div>
              )}

              {/* Oppure aggiungi manualmente */}
              <div className="border-t border-slate-700/50 lg:border-gray-200 pt-4">
                <p className="text-xs text-slate-400 lg:text-gray-500 mb-3">
                  Oppure aggiungi manualmente senza DDT:
                </p>
                <button
                  onClick={aggiungiIngredienteManuale}
                  data-testid="nuovo-ingrediente-manuale-btn"
                  disabled={!nuovoIngredienteNome.trim()}
                  className="w-full py-2.5 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-slate-700 lg:border-transparent"
                >
                  <PlusCircle className="h-4 w-4" />
                  Aggiungi &quot;{nuovoIngredienteNome || '...'}&quot; senza DDT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Impostazioni Etichetta - FULLSCREEN su mobile */}
      {showSettingsModal && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[70]">
          {/* Mobile Background Image */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-lg lg:max-h-[90vh] overflow-hidden flex flex-col relative z-10">
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-gray-50 lg:to-gray-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowSettingsModal(false)} 
                  className="p-2 bg-slate-800 lg:hidden hover:bg-slate-700 rounded-xl"
                >
                  <ChevronLeft className="h-5 w-5 text-white" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Impostazioni Etichetta</h3>
                  <p className="text-xs text-slate-400 lg:text-gray-500">Configura logo e layout</p>
                </div>
              </div>
              <button 
                onClick={() => setShowSettingsModal(false)} 
                className="p-1.5 bg-slate-700 lg:bg-transparent hover:bg-slate-600 lg:hover:bg-gray-200 rounded-lg hidden lg:block"
              >
                <X className="h-4 w-4 text-white lg:text-gray-500" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-32 lg:pb-4">
              {/* Upload Logo */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700">
                  Logo Aziendale (JPG/PNG, max 500KB)
                </label>
                
                {logoPreview ? (
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 border-2 border-slate-700/50 lg:border-gray-200 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                      <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-400 lg:text-gray-600 mb-2">{logoSettings.logo_filename || 'logo.png'}</p>
                      <div className="flex gap-2">
                        <label className="cursor-pointer px-3 py-1.5 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-lg text-xs font-medium border border-slate-700 lg:border-transparent">
                          <input type="file" accept=".jpg,.jpeg,.png" onChange={handleLogoUpload} className="hidden" />
                          Cambia
                        </label>
                        <button
                          onClick={removeLogo}
                          className="px-3 py-1.5 bg-red-500/20 lg:bg-red-50 hover:bg-red-500/30 lg:hover:bg-red-100 text-red-400 lg:text-red-600 rounded-lg text-xs font-medium"
                        >
                          Rimuovi
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer block border-2 border-dashed border-slate-700/50 lg:border-gray-300 rounded-xl p-6 text-center hover:border-blue-500 lg:hover:border-blue-400 hover:bg-slate-800 lg:hover:bg-blue-50 transition-colors">
                    <input type="file" accept=".jpg,.jpeg,.png" onChange={handleLogoUpload} className="hidden" />
                    <Upload className="h-8 w-8 text-slate-500 lg:text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-400 lg:text-gray-600">Clicca per caricare il logo</p>
                    <p className="text-xs text-slate-500 lg:text-gray-400 mt-1">JPG o PNG, max 500KB</p>
                  </label>
                )}
              </div>

              {/* Posizione Logo */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700">Posizione Logo</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'alto-sinistra', label: 'Alto Sinistra' },
                    { value: 'alto-destra', label: 'Alto Destra' },
                    { value: 'basso-sinistra', label: 'Basso Sinistra' },
                    { value: 'basso-destra', label: 'Basso Destra' }
                  ].map(pos => (
                    <button
                      key={pos.value}
                      onClick={() => setLogoSettings(prev => ({ ...prev, logo_posizione: pos.value }))}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                        logoSettings.logo_posizione === pos.value
                          ? 'border-blue-500 bg-blue-600/20 lg:bg-blue-50 text-blue-400 lg:text-blue-700'
                          : 'border-slate-700/50 lg:border-gray-200 hover:border-slate-600 lg:hover:border-gray-300 text-slate-400 lg:text-gray-600'
                      }`}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dimensione Logo */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700">
                  Dimensione Logo: {logoSettings.logo_size}mm
                </label>
                <input
                  type="range"
                  min="5"
                  max="20"
                  value={logoSettings.logo_size}
                  onChange={(e) => setLogoSettings(prev => ({ ...prev, logo_size: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-slate-700 lg:bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-slate-500 lg:text-gray-400">
                  <span>5mm</span>
                  <span>20mm</span>
                </div>
              </div>

              {/* Anteprima */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700">Anteprima Etichetta</label>
                <div className="border-2 border-slate-700/50 lg:border-gray-200 rounded-xl p-4 bg-black/40 backdrop-blur-md lg:bg-gray-50">
                  <div className="relative w-full aspect-[2/1] bg-white border border-gray-300 rounded-lg overflow-hidden">
                    {/* Simulazione etichetta */}
                    <div className="absolute top-0 left-0 right-0 h-[24%] border-b border-gray-300 flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-800">NOME PRODOTTO</span>
                    </div>
                    <div className="absolute top-[24%] left-[4%] w-[28%] h-[56%] border border-gray-300 bg-gray-100 flex items-center justify-center">
                      <QrCode className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="absolute top-[28%] left-[36%] right-[4%] text-[6px] text-gray-500 space-y-0.5">
                      <div className="border border-gray-300 px-1 py-0.5 font-bold">LOTTO: 001/2026</div>
                      <div className="mt-1">DATA PROD: 08/01/2026</div>
                      <div className="font-bold text-[5px]">DA CONSUMARSI PREF. ENTRO:</div>
                      <div className="font-bold">15/01/2026</div>
                    </div>
                    {/* Logo preview */}
                    {logoPreview && (
                      <div 
                        className={`absolute w-[${logoSettings.logo_size}%] h-[${logoSettings.logo_size * 2}%] ${
                          logoSettings.logo_posizione === 'alto-sinistra' ? 'top-[4%] left-[4%]' :
                          logoSettings.logo_posizione === 'alto-destra' ? 'top-[4%] right-[4%]' :
                          logoSettings.logo_posizione === 'basso-sinistra' ? 'bottom-[4%] left-[4%]' :
                          'bottom-[4%] right-[4%]'
                        }`}
                        style={{ width: `${logoSettings.logo_size * 2}%`, height: `${logoSettings.logo_size * 4}%` }}
                      >
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 lg:text-gray-400 text-center mt-2">Anteprima schematica (100x50mm)</p>
                </div>
                <button
                  onClick={downloadAnteprimaEtichetta}
                  className="w-full py-2 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2 border border-slate-700 lg:border-transparent"
                >
                  <Eye className="h-4 w-4" />
                  Scarica Anteprima PDF
                </button>
              </div>
            </div>

            {/* Footer - FISSO in basso su mobile SOPRA la bottom nav */}
            <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-4 border-t border-slate-700/50 lg:border-gray-100 flex gap-3 bg-black/60 backdrop-blur-md lg:bg-white z-[71]">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 py-3 lg:py-2.5 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium text-sm border border-slate-700 lg:border-transparent"
              >
                Annulla
              </button>
              <button
                onClick={saveLogoSettings}
                disabled={savingSettings}
                className="flex-1 py-3 lg:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingSettings ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Salva
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Storico DDT per ingrediente */}
      {showStoricoDdtModal && storicoDdtIngrediente && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[120] p-4">
          <div className="bg-black/60 backdrop-blur-md lg:bg-white/95 rounded-2xl w-full max-w-lg overflow-hidden border border-slate-700 lg:border-transparent max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-slate-700/50 lg:border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-semibold text-white lg:text-gray-900 flex items-center gap-2">
                  <History className="h-5 w-5 text-purple-400 lg:text-purple-600" />
                  Storico DDT
                </h3>
                <p className="text-xs text-slate-400 lg:text-gray-500 mt-1">
                  Seleziona il DDT per: <span className="font-medium text-blue-400 lg:text-blue-600">{storicoDdtIngrediente.nome_prodotto_ddt || storicoDdtIngrediente.nome_ingrediente}</span>
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowStoricoDdtModal(false);
                  setStoricoDdtIngrediente(null);
                  setStoricoDdtResults([]);
                }} 
                className="p-2 bg-black/40 backdrop-blur-md lg:bg-transparent hover:bg-slate-700 lg:hover:bg-gray-100 rounded-lg"
              >
                <X className="h-4 w-4 text-slate-400 lg:text-gray-500" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {loadingStoricoDdt ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : storicoDdtResults.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-slate-600 lg:text-gray-300 mx-auto mb-3" />
                  <p className="text-slate-400 lg:text-gray-500">Nessun DDT trovato per questo prodotto</p>
                  <p className="text-xs text-slate-500 lg:text-gray-400 mt-1">Prova a cercare con un nome diverso</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-slate-400 lg:text-gray-500 mb-2">
                    Ultimi {storicoDdtResults.length} DDT contenenti questo prodotto (FIFO - First In First Out):
                  </p>
                  {storicoDdtResults.map((ddt, idx) => (
                    <div 
                      key={idx}
                      onClick={() => selezionaDdtAlternativo(ddt)}
                      className="p-3 bg-slate-700/50 lg:bg-gray-50 rounded-xl border border-slate-600 lg:border-gray-200 hover:border-purple-500 lg:hover:border-purple-400 cursor-pointer transition-all hover:scale-[1.01]"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-white lg:text-gray-900 text-sm">{ddt.nome_prodotto}</p>
                          <p className="text-xs text-slate-400 lg:text-gray-500">{ddt.fornitore}</p>
                        </div>
                        <span className="text-xs font-medium px-2 py-1 bg-purple-500/20 lg:bg-purple-100 text-purple-400 lg:text-purple-700 rounded-lg">
                          DDT #{ddt.ddt_numero}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-slate-500 lg:text-gray-400" />
                          <span className="text-slate-400 lg:text-gray-500">Data:</span>
                          <span className="font-medium text-white lg:text-gray-700">{ddt.data_ddt || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Package className="h-3 w-3 text-slate-500 lg:text-gray-400" />
                          <span className="text-slate-400 lg:text-gray-500">Lotto:</span>
                          <span className="font-medium text-white lg:text-gray-700">{ddt.numero_lotto || 'N/A'}</span>
                        </div>
                        {ddt.scadenza && (
                          <div className="flex items-center gap-1 col-span-2">
                            <AlertTriangle className="h-3 w-3 text-orange-500" />
                            <span className="text-slate-400 lg:text-gray-500">Scadenza:</span>
                            <span className="font-medium text-orange-400 lg:text-orange-600">{ddt.scadenza}</span>
                          </div>
                        )}
                        {ddt.quantita && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 lg:text-gray-500">Qtà:</span>
                            <span className="font-medium text-white lg:text-gray-700">{ddt.quantita} {ddt.unita_misura || ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-slate-700/50 lg:border-gray-100 flex-shrink-0">
              <button
                onClick={() => {
                  setShowStoricoDdtModal(false);
                  setStoricoDdtIngrediente(null);
                  setStoricoDdtResults([]);
                }}
                className="w-full py-2.5 bg-black/40 backdrop-blur-md lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium text-sm border border-slate-700 lg:border-transparent"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EtichetteConservazioneSection;
